/**
 * Card Grading Service JavaScript V3 (Optimized Flow)
 * V3: grade + centering in parallel, recognition deferred until grade completes
 * so we can call the correct endpoint (sport_id vs tcg_id).
 */

// 2026-10-21: real-device JS error instrumentation. The "saw the form but never
// tapped" cohort has zero console evidence — headless audits pass clean, so the
// failure (if any) is device-specific. Route every uncaught error + unhandled
// rejection to the server (same sink the camera errors use) so the next
// occurrence carries a console trace we can actually read.
// pgc-diag.js now installs the same hook globally on every page class; when it
// ran first we stand down (it sets __pgcErrHooked) rather than double-report.
(function () {
    if (window.__pgcErrHooked) return;
    window.__pgcErrHooked = true;
    function report(kind, msg, src, line, col, err) {
        try {
            // CefSharp email-scanner rejection ("Object Not Found Matching
            // Id:N, MethodName:update, ParamCount:4") — bot noise, not ours.
            if (/object not found matching id:\d+/i.test(String(msg || ''))) return;
            // Bare "invalid origin" rejections — DuckDuckGo iOS injects an
            // origin-checked content-script bridge whose rejections surface
            // here (#194108). Mirrors pgc-diag's filter; Errors with a real
            // stack still report.
            if (/^invalid origin$/i.test(String(msg || '')) && !src && !(err && err.stack)) return;
            var detail = '';
            if (err && err.stack) detail = String(err.stack).slice(0, 500);
            navigator.sendBeacon('/api/upload-log', new Blob([JSON.stringify({
                stage: kind === 'unhandledrejection' ? 'promise_reject' : 'js_error',
                error: String(msg || '').slice(0, 200),
                pageUrl: location.pathname + location.search,
                extra: {
                    channel: 'grade-v3-js',
                    source: String(src || '').slice(0, 200),
                    line: line, col: col,
                    stack: detail,
                    ua: navigator.userAgent.slice(0, 200)
                }
            })], { type: 'application/json' }));
        } catch (e) {}
    }
    window.addEventListener('error', function (e) {
        report('error', e.message, e.filename, e.lineno, e.colno, e.error);
    });
    window.addEventListener('unhandledrejection', function (e) {
        var r = e.reason || {};
        report('unhandledrejection', (r && r.message) || String(r), '', 0, 0, r instanceof Error ? r : null);
    });
})();

// Grade-attempt correlation. Retry chains (reject → retake → reject) were
// only joinable by timestamp guessing; att makes each attempt's full
// lifecycle — submit, beam, response, reject, retry — a single-key lookup.
var __pgcAttemptId = null;
function pgcNewAttempt() {
    __pgcAttemptId = 'ga' + Date.now().toString(36) + Math.floor(Math.random() * 1679616).toString(36);
    return __pgcAttemptId;
}
// Shared emitter — the submit handler's local `funnel` const doesn't reach
// top-level helpers (showUploadRejectedModal's typeof-funnel guard silently
// evaluated false, so photo_reject_* events never emitted).
function pgcGradeEvent(type, meta) {
    if (typeof logFunnelEvent !== 'function') return;
    var m = meta || {};
    if (__pgcAttemptId && m.att === undefined) m.att = __pgcAttemptId;
    logFunnelEvent(type, m);
}

// bfcache restores resurrect the page with the OLD baked CREDIT_BALANCE
// (Safari restores even no-store pages) — refresh it so the zero-balance
// pre-check and the back-addon note act on the real number.
window.addEventListener('pageshow', function (e) {
    if (!e.persisted || !window.IS_LOGGED_IN) return;
    fetch('/api/credit-status.php', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (j) { if (j && j.success && typeof j.balance === 'number') window.CREDIT_BALANCE = j.balance; })
        .catch(function () {});
});

const GRADING_FACTS = [
    pgcT('gm.fact.1', 'PSA grades cards on a 1-10 scale; 10 means Gem Mint with no visible flaws.'),
    pgcT('gm.fact.2', 'Centering is measured to the millimeter — even 55/45 can drop a grade.'),
    pgcT('gm.fact.3', 'Surface flaws like print dots, scratches, and clouding are the #1 reason cards lose points.'),
    pgcT('gm.fact.4', 'Corner wear is magnified under professional grading lamps.'),
    pgcT('gm.fact.5', 'A PSA 10 can be worth 5–10x more than the same card in PSA 9.'),
    pgcT('gm.fact.6', 'BGS uses half-point grades and four sub-grades: corners, edges, surface, centering.'),
    pgcT('gm.fact.7', 'CGC is popular for Pokémon and TCG cards with budget-friendly pricing.'),
    pgcT('gm.fact.8', 'SGC is known for being vintage-friendly and using a black tuxedo label.'),
    pgcT('gm.fact.9', 'Whitening on card edges is one of the first things graders look for.'),
    pgcT('gm.fact.10', 'AI pre-grading can help you avoid $25–$150 fees on cards that will not gem.'),
    pgcT('gm.fact.11', 'The four pillars of grading: centering, corners, edges, and surface.'),
    pgcT('gm.fact.12', 'Subgrades are a roadmap — one low subgrade often caps the final grade.'),
    pgcT('gm.fact.13', 'A 60/40 centering split is the threshold for PSA 10 consideration.'),
    pgcT('gm.fact.14', 'Print defects like fish eyes and snow are common in modern cards.'),
    pgcT('gm.fact.15', 'Edge silvering occurs when the foil layer separates from the card stock.'),
    pgcT('gm.fact.16', 'A card with a 10 on three subgrades but an 8 on one will typically cap at a 9.'),
    pgcT('gm.fact.17', 'PSA has graded over 50 million cards since its founding in 1991.'),
    pgcT('gm.fact.18', 'Gem Mint PSA 10s of modern cards can still sell for thousands.'),
    pgcT('gm.fact.19', 'Lighting matters: graded lamps reveal flaws invisible to the naked eye.'),
    pgcT('gm.fact.20', 'Card sleeves can cause micro-scratches — always remove before grading.'),
    pgcT('gm.fact.21', 'A BGS Black Label (10/10/10/10) is rarer than a PSA 10.'),
    pgcT('gm.fact.22', 'Offset printing can cause misaligned borders, hurting centering scores.'),
    pgcT('gm.fact.23', 'Surface clouding is often caused by moisture trapped in the card stock.'),
    pgcT('gm.fact.24', 'Vintage cards (pre-1980) are graded more leniently on centering.'),
    pgcT('gm.fact.25', 'A 9.5 BGS Pristine with a 10 auto subgrade can rival a PSA 10 in value.'),
    pgcT('gm.fact.26', 'Corner dings are the most common reason a card misses PSA 10.'),
    pgcT('gm.fact.27', 'AI grading models are trained on millions of previously graded cards.'),
    pgcT('gm.fact.28', 'The difference between a PSA 9 and PSA 10 can be a single print dot.'),
    pgcT('gm.fact.29', 'Holofoil cards are more prone to surface scratches than non-holo cards.'),
    pgcT('gm.fact.30', 'Grading companies use calibrated microscopes to inspect corner sharpness.'),
    pgcT('gm.fact.31', 'A card graded 8.5 or higher is generally worth submitting professionally.'),
    pgcT('gm.fact.32', 'Condition-sensitive cards like 1989 Upper Deck Ken Griffey Jr. are notoriously hard to grade.')
];

const COUNTDOWN_DEFAULT = 90;

// Provider-health flag — grade.php renders window.PGC_XIM_STATUS from the
// cron status file (data/ximilar_status.json) when the Ximilar queue is
// warm/degraded. Drives honest provider-aware copy only; it never gates a
// submission. Fails open to 'ok' when the flag is absent.
// Status older than 20 min (edge-cached HTML) is ignored.
const PGC_XIM_FRESH = !!(window.PGC_XIM_STATUS && window.PGC_XIM_STATUS.ts && (Date.now() / 1000 - window.PGC_XIM_STATUS.ts) < 1200);
const PGC_XIM = (PGC_XIM_FRESH && window.PGC_XIM_STATUS.verdict) || 'ok';
// Measured p75 end-to-end grade time (successful grades, last 24h) from the
// watchdog — replaces the static 90-170s guess that overshot the real p50 of
// ~54s (2026-11-02 measurement) and made the countdown meaningless.
const PGC_GRADE_ETA = (PGC_XIM_FRESH && Number(window.PGC_XIM_STATUS.eta_s)) || 0;
const PGC_XIM_SLOW = PGC_XIM === 'warm' || PGC_XIM === 'degraded';

function estimateCountdown(width, height, hasBack) {
    const mp = (width && height) ? (width * height) / 1000000 : 0;
    if (PGC_GRADE_ETA > 0) {
        // Live measured baseline (p75) + small size/back terms, bounded.
        return Math.max(45, Math.min(150, Math.round(PGC_GRADE_ETA + 4 * mp + (hasBack ? 15 : 0))));
    }
    if (!width || !height) return COUNTDOWN_DEFAULT;
    // V3 hybrid: grade + centering + sport_id/tcg_id + market comps lookups.
    // Production data (254 successful grades, Oct 2026, 2d window):
    //   avg 75.9s; 79% exceed 60s; 21% exceed 90s; ~4% exceed 120s.
    //   Ximilar upstream degraded vs Sep (was median 47.9s, ~13% >75s) —
    //   single provider calls now run 60-130s inside the GRADE_TOTAL_BUDGET
    //   cap, plus comps (~10-45s) and iq/card_id collected alongside.
    // Oct 21: ALL sold comps providers re-enabled + the eBay sold leg — the
    // comps tail now runs parallel scrapers + provider cooldown waits, adding
    // roughly 20-40s to the typical grade (~90-140s end to end). Target
    // ~P75-P90 coverage: base 80s + 20s/MP, +20s when a back photo adds a
    // second grade record. A 2MP front+back predicts ~140s.
    const predictedSec = Math.round(80 + 20 * mp + (hasBack ? 20 : 0));
    return Math.max(90, Math.min(170, predictedSec));
}

function getGradeTheme(grade) {
    const num = parseFloat(grade);
    if (isNaN(num)) return { color: '#a78bfa', glow: 'rgba(167,139,250,0.5)', icon: '❓', label: 'Unknown', bgGradient: 'linear-gradient(135deg, #4c1d95 0%, #2e1065 100%)', confettiCount: 20 };
    if (num >= 9.5) return { color: '#fbbf24', glow: 'rgba(251,191,36,0.65)', icon: '💎', label: 'Gem Mint', bgGradient: 'linear-gradient(135deg, #78350f 0%, #451a03 100%)', confettiCount: 80, rainbow: true };
    if (num >= 9) return { color: '#34d399', glow: 'rgba(52,211,153,0.6)', icon: '🌟', label: 'Mint', bgGradient: 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)', confettiCount: 60 };
    if (num >= 8) return { color: '#22d3ee', glow: 'rgba(34,211,238,0.55)', icon: '⚡', label: 'NM-MT', bgGradient: 'linear-gradient(135deg, #155e75 0%, #083344 100%)', confettiCount: 45 };
    if (num >= 7) return { color: '#a3e635', glow: 'rgba(163,230,53,0.5)', icon: '✨', label: 'Near Mint', bgGradient: 'linear-gradient(135deg, #3f6212 0%, #1a2e05 100%)', confettiCount: 35 };
    if (num >= 5) return { color: '#fb923c', glow: 'rgba(251,146,60,0.55)', icon: '🔥', label: 'Excellent', bgGradient: 'linear-gradient(135deg, #7c2d12 0%, #431407 100%)', confettiCount: 30 };
    return { color: '#f472b6', glow: 'rgba(244,114,182,0.5)', icon: '🎨', label: 'Authentic', bgGradient: 'linear-gradient(135deg, #831843 0%, #500724 100%)', confettiCount: 25 };
}

function resetUploadZone(zone) {
    if (!zone) return;
    const input = zone.querySelector('input[type="file"]');
    const placeholder = zone.querySelector('.upload-placeholder');
    const preview = zone.querySelector('.upload-preview');
    if (input) { input.value = ''; input._heicFile = null; }
    if (preview) {
        const img = preview.querySelector('img');
        if (img) img.removeAttribute('src');
        preview.style.display = 'none';
    }
    if (placeholder) placeholder.style.display = zone.dataset.phDisplay || 'block';
    zone.classList.remove('has-file');
}

function resetUploadForm() {
    const form = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
    if (!form) return;
    form.reset();
    document.querySelectorAll('.upload-zone').forEach(resetUploadZone);
    const mainSubmit = form.querySelector('.scan-card-main');
    if (mainSubmit) mainSubmit.style.display = 'block';
    if (typeof syncSubmitButtons === 'function') syncSubmitButtons(form);
}

function showGradingModal(cardPreviewUrl, countdownSec) {
    // +5s display pad so on-time grades land "early" — finishing under the
    // estimate reads as overperforming; the overrun state still triggers at 0.
    const COUNTDOWN_START = (countdownSec || COUNTDOWN_DEFAULT) + 5;
    // Hide promo footer and sticky CTA so they don't cover the modal
    document.querySelectorAll('.pgc-promo, #sticky-mini-cta').forEach(el => el.style.display = 'none');
    // Dismiss leftover toasts (e.g. the stash "still attached" notice) so a
    // stale one doesn't stack under the grading modal or its outcome toast.
    document.querySelectorAll('.toast, .pgc-toast, [class*="toastify"], [role="alert"]').forEach(el => el.remove());

    const existing = document.getElementById('grading-loading-modal');
    if (existing) {
        if (typeof existing._beamDestroy === 'function') existing._beamDestroy();
        if (typeof existing._stopGradeProgressPoll === 'function') existing._stopGradeProgressPoll();
        const intervals = JSON.parse(existing.dataset.intervals || '{}');
        clearInterval(intervals.fact);
        clearInterval(intervals.status);
        clearInterval(intervals.countdown);
        const objectUrl = existing.dataset.objectUrl;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        if (existing._fitCard) window.removeEventListener('resize', existing._fitCard);
        existing.remove();
    }

    let modal = document.createElement('div');
    modal.id = 'grading-loading-modal';
    modal.innerHTML = `
            <div class="glm-backdrop" style="position:fixed; inset:0; background:#0f172a; z-index: 99999; display: flex; align-items: center; justify-content: center; padding: 24px; opacity: 0; transition: opacity 0.4s ease;">
                <div class="glm-card" style="background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%); border: 1px solid rgba(255,255,255,0.08); border-radius: 24px; padding: clamp(22px, 5vh, 48px) clamp(20px, 5vw, 40px); max-width: 480px; width: 100%; text-align: center; box-shadow: 0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(59,130,246,0.15); position: relative; overflow-x: hidden; overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; max-height: calc(100vh - 24px); max-height: calc(100dvh - 24px); transform: translateY(16px) scale(0.96); transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);">
                    <div id="glm-content" style="position: relative; z-index: 1;">
                        <div class="glm-preview" style="width: 120px; height: 168px; margin: 0 auto 28px; border-radius: 0; position: relative; overflow: hidden; box-shadow: 0 25px 45px rgba(15,23,42,0.55), 0 0 0 1px rgba(148,163,184,0.25); background: linear-gradient(135deg, rgba(59,130,246,0.12), rgba(124,58,237,0.08)); display: flex; align-items: center; justify-content: center;">
                            <img id="glm-card-image" alt="Uploaded card preview" style="width: 100%; height: 100%; object-fit: cover; border-radius: 0; opacity: 0; transition: opacity 0.4s ease;" />
                            <div class="glm-preview-fallback" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; transition: opacity 0.3s ease;">
                                <div style="width: 100px; height: 140px; background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); border-radius: 14px; box-shadow: 0 20px 40px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; animation: glmGlow 2s ease-in-out infinite alternate;">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5" style="opacity: 0.95;">
                                        <rect x="3" y="5" width="18" height="14" rx="2"/>
                                        <path d="M3 10h18"/>
                                        <path d="M8 14h.01"/>
                                        <path d="M12 14h.01"/>
                                        <path d="M16 14h.01"/>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <h2 style="color: white; font-size: 22px; font-weight: 800; margin: 0 0 12px; letter-spacing: -0.01em;">${pgcT('gm.analyzing', 'Analyzing your card...')}</h2>
                        <p id="glm-status" style="color: #94a3b8; font-size: 14px; margin: 0 0 20px; min-height: 44px; line-height: 1.55; transition: opacity 0.3s ease;">Running parallel AI vision across corners, edges, surface, and centering.</p>
                        <div id="glm-progress" style="display:none; margin: 0 0 18px; text-align: left;"></div>
                        <div id="glm-countdown-wrap" style="margin: 0 0 20px; display: flex; flex-direction: column; align-items: center; gap: 8px;">
                            <div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
                                <div id="glm-countdown-ring" style="width: 64px; height: 64px; position: relative; display: flex; align-items: center; justify-content: center;">
                                    <svg width="64" height="64" viewBox="0 0 64 64" style="transform: rotate(-90deg); position: absolute; inset: 0;">
                                        <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="4"/>
                                        <circle id="glm-countdown-circle" cx="32" cy="32" r="28" fill="none" stroke="url(#glm-countdown-grad)" stroke-width="4" stroke-linecap="round" stroke-dasharray="175.929" stroke-dashoffset="0" style="transition: stroke-dashoffset 1s linear, stroke 0.5s ease;"/>
                                        <defs>
                                            <linearGradient id="glm-countdown-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" style="stop-color:#22c55e"/>
                                                <stop offset="100%" style="stop-color:#3b82f6"/>
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <span id="glm-countdown-num" style="font-size: 22px; font-weight: 800; color: white; font-variant-numeric: tabular-nums; position: relative; z-index: 1;">${COUNTDOWN_START}</span>
                                </div>
                                <div style="text-align: left;">
                                    <div style="font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b;">${pgcT('gm.est.time', 'Estimated Time')}</div>
                                    <div id="glm-countdown-label" style="font-size: 13px; color: #94a3b8;">${pgcT('gm.sec.rem', 'seconds remaining')}</div>
                                </div>
                            </div>
                        </div>
                        <div id="glm-fact" style="background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.18); border-radius: 12px; padding: 14px 16px; color: #dcfce7; font-size: 13px; line-height: 1.5; min-height: 66px; display: flex; align-items: center; justify-content: center; transition: opacity 0.4s ease, transform 0.4s ease;">
                            <span>💡</span> <span style="margin-left: 8px;">${getRandomFact()}</span>
                        </div>
                    </div>
                </div>
            </div>
            <style>
                @keyframes glmPulse { 0%, 100% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.1); opacity: 0.8; } }
                @keyframes glmChipPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.55); opacity: 1; } 50% { box-shadow: 0 0 0 7px rgba(251,191,36,0); opacity: 0.7; } }
                @keyframes glmGlow { 0% { box-shadow: 0 20px 40px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.2); } 100% { box-shadow: 0 25px 50px rgba(139,92,246,0.45), inset 0 1px 0 rgba(255,255,255,0.25); } }
                @keyframes glmShimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
                @keyframes glmRevealPop { 0% { transform: scale(0.3) rotate(-15deg); opacity: 0; } 50% { transform: scale(1.15) rotate(3deg); opacity: 1; } 70% { transform: scale(0.95) rotate(-1deg); } 100% { transform: scale(1) rotate(0); opacity: 1; } }
                @keyframes glmConfettiFall { 0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }
                @keyframes glmBurstGlow { 0% { box-shadow: 0 0 0 0 var(--burst-color); } 50% { box-shadow: 0 0 40px 20px var(--burst-color); } 100% { box-shadow: 0 0 0 0 var(--burst-color); } }
                @keyframes glmScaleIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
                @keyframes glmSlideUp { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
                @keyframes glmPulseGrade { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
            </style>
        `;
        document.body.appendChild(modal);

    modal.style.display = 'block';
    const backdrop = modal.querySelector('.glm-backdrop');
    const card = modal.querySelector('.glm-card');
    const status = modal.querySelector('#glm-status');
    const fact = modal.querySelector('#glm-fact');
    const previewImg = modal.querySelector('#glm-card-image');
    const fallbackCover = modal.querySelector('.glm-preview-fallback');
    const countdownNum = modal.querySelector('#glm-countdown-num');
    const countdownCircle = modal.querySelector('#glm-countdown-circle');
    const countdownLabel = modal.querySelector('#glm-countdown-label');
    const countdownWrap = modal.querySelector('#glm-countdown-wrap');

    void backdrop.offsetWidth;
    backdrop.style.opacity = '1';
    card.style.transform = 'translateY(0) scale(1)';

    // Fit-to-viewport: shrink the whole card (CSS zoom scales layout, unlike
    // transform) so all reveal content stays visible — never a scroll area.
    // max-height must be lifted while zoomed, otherwise the capped box still
    // overflows internally and shows a scrollbar.
    const fitCard = () => {
        card.style.zoom = '1';
        card.style.maxHeight = 'none';
        const z = (window.innerHeight - 32) / card.offsetHeight;
        if (z >= 1) {
            card.style.maxHeight = '';  // fits naturally — keep the CSS cap
        } else if (z >= 0.45) {
            card.style.zoom = String(z);  // shrink to fit; maxHeight stays 'none'
        } else {
            card.style.maxHeight = '';  // absurd screen: cap + scroll as last resort
        }
    };
    modal._fitCard = fitCard;
    window.addEventListener('resize', fitCard);
    fitCard();

    if (previewImg) {
        const existingUrl = modal.dataset.objectUrl;
        if (existingUrl && existingUrl !== cardPreviewUrl) {
            URL.revokeObjectURL(existingUrl);
            delete modal.dataset.objectUrl;
        }

        if (cardPreviewUrl) {
            previewImg.style.opacity = '0';
            previewImg.onload = () => {
                const previewContainer = previewImg.closest('.glm-preview');
                if (previewContainer) {
                    const isLandscape = previewImg.naturalWidth > previewImg.naturalHeight;
                    previewContainer.style.width = isLandscape ? '168px' : '120px';
                    previewContainer.style.height = isLandscape ? '120px' : '168px';
                }
                previewImg.style.opacity = '1';
                if (fallbackCover) {
                    fallbackCover.style.opacity = '0';
                }
            };
            previewImg.onerror = () => {
                previewImg.style.opacity = '0';
                if (fallbackCover) {
                    fallbackCover.style.opacity = '1';
                }
            };
            previewImg.src = cardPreviewUrl;
            modal.dataset.objectUrl = cardPreviewUrl;
            if (fallbackCover) {
                fallbackCover.style.opacity = '0';
            }
        } else {
            previewImg.removeAttribute('src');
            previewImg.style.opacity = '0';
            if (fallbackCover) {
                fallbackCover.style.opacity = '1';
            }
        }
    }

    const factInterval = setInterval(() => {
        fact.style.opacity = '0';
        fact.style.transform = 'translateY(8px)';
        setTimeout(() => {
            const nextIndex = Math.floor(Math.random() * GRADING_FACTS.length);
            fact.querySelector('span:last-child').textContent = GRADING_FACTS[nextIndex];
            fact.style.opacity = '1';
            fact.style.transform = 'translateY(0)';
        }, 400);
    }, 10500);

    const statusMessages = [
        pgcT('gm.s1', 'Running parallel AI vision across corners, edges, surface, and centering.'),
        pgcT('gm.s2', 'Overlapping centering analysis with grade polling...'),
        pgcT('gm.s3', 'Fetching market data while generating visual overlays...'),
        pgcT('gm.s4', 'Finalizing scores and building your report...')
    ];
    let statusIndex = 0;
    const statusInterval = setInterval(() => {
        statusIndex = (statusIndex + 1) % statusMessages.length;
        status.style.opacity = '0';
        setTimeout(() => {
            status.textContent = statusMessages[statusIndex];
            status.style.opacity = '1';
        }, 300);
    }, 8400);

    let countdownVal = COUNTDOWN_START;
    const ringCircumference = 175.929;
    const countdownInterval = setInterval(() => {
        countdownVal--;
        if (countdownNum) countdownNum.textContent = Math.max(countdownVal, 0);
        if (countdownCircle) {
            const offset = ringCircumference * (1 - countdownVal / COUNTDOWN_START);
            countdownCircle.style.strokeDashoffset = offset;
            const warnThreshold = Math.max(4, Math.round(COUNTDOWN_START * 0.2));
            const dangerThreshold = Math.max(2, Math.round(COUNTDOWN_START * 0.1));
            if (countdownVal <= warnThreshold && countdownVal > dangerThreshold) {
                countdownCircle.style.stroke = '#f59e0b';
                if (countdownNum) countdownNum.style.color = '#f59e0b';
            } else if (countdownVal <= dangerThreshold && countdownVal > 0) {
                countdownCircle.style.stroke = '#ef4444';
                if (countdownNum) countdownNum.style.color = '#ef4444';
            }
        }
        if (countdownVal <= 0) {
            if (!modal.dataset.overrun) {
                // Past the estimate — the grade is still running in the poll.
                // A frozen "0" + full ring looks stalled for the 30-70s the
                // tail can run; switch to an indeterminate state instead.
                modal.dataset.overrun = '1';
                if (countdownNum) countdownNum.style.display = 'none';
                const ring = modal.querySelector('#glm-countdown-ring svg');
                if (ring) ring.style.animation = 'glmPulse 1.6s ease-in-out infinite';
                if (countdownLabel) {
                    countdownLabel.textContent = 'Still working — hang tight';
                    countdownLabel.style.color = '#fbbf24';
                    countdownLabel.style.fontWeight = '700';
                }
            }
        }
    }, 1000);

    modal.dataset.intervals = JSON.stringify({ fact: factInterval, status: statusInterval, countdown: countdownInterval });

    const backToTop = document.querySelector('.back-to-top');
    if (backToTop) {
        backToTop.classList.add('modal_hidden');
        backToTop.style.setProperty('display', 'none', 'important');
    }

    return modal;
}

// ── Mid-flight "card identified" progress + blueprint-scan overlay ──
// The modal previously spun static status copy until the WHOLE pipeline
// landed (comps tail can run 40-90s). The client polls grade-status'
// progress payload and reveals the identified card as soon as the arbiter
// settles — showing real progress instead of a blank wait.
const PGC_GRADE_PROGRESS_DEMO = window.PGC_GRADE_PROGRESS_DEMO === '1'
    || /[?&]demo=1([&#]|$)/.test(window.location.search)
    || (function () { try { return document.cookie.split('; ').some(function (c) { return c.indexOf('pgc_demo=1') === 0; }); } catch (e) { return false; } })();

function startGradeProgressPoll(sinceSec, submitStartMs) {
    const modal = document.getElementById('grading-loading-modal');
    if (!modal) return;
    const panel = modal.querySelector('#glm-progress');
    if (!panel) return;
    // Chip stagger keyframes — inject once (guarded by id).
    if (!document.getElementById('pgc-chip-in-style')) {
        const st = document.createElement('style');
        st.id = 'pgc-chip-in-style';
        st.textContent = '@keyframes glmChipIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}';
        (document.head || document.documentElement).appendChild(st);
    }
    panel.style.display = 'block';
    panel.innerHTML = '<div style="color:#94a3b8;font-size:12px;line-height:1.6;">'
        + '<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#3b82f6;margin-right:8px;animation:glmPulse 1.2s ease-in-out infinite;"></span>'
        + 'Submitting your card…</div>';
    // The panel owns status in demo mode — hide the stock status line and
    // rotating fact box outright so the enlarged scan preview still fits
    // without shrinking the whole modal.
    const glmStatus = modal.querySelector('#glm-status');
    if (glmStatus) glmStatus.style.display = 'none';
    const glmFact = modal.querySelector('#glm-fact');
    if (glmFact) glmFact.style.display = 'none';

    let progressTimer = null;
    let seenInFlight = false;
    // Wait heartbeat — "how long did they sit in the grading modal before
    // leaving" was pure timestamp inference (u4896's 36s wait). Emits at
    // 15/45/90s with the best-known stage, and fires even when the status
    // request itself fails since it doesn't read the payload.
    const WAIT_MARKS = [15, 45, 90];
    let waitMark = 0;
    let shownKey = null;
    let shownKind = null;
    let shownAt = 0;
    let lastCard = null;
    // Paced chip reveal. prog.done is server truth, but payloads land in
    // bursts (dwell gate + 3s poll coalesce several writes), and flipping
    // every done chip in one render reads as "all at once". Chips instead
    // flip one at a time on a timer — each gated on the displayed phase
    // having actually reached it, so a chip can never lead the panel.
    const chipShown = {};
    let chipTarget = [];
    let lastChipFlip = 0;
    const CHIP_ORDER = ['detected', 'identified', 'pillars', 'market'];
    const CHIP_PHASE = { detected: 1, identified: 2, pillars: 2, market: 3 };
    const CHIP_FLIP_MS = 2400; // min gap between chip fills — distinct beats
    const curPhase = () => shownKind === 'detected' ? 1
        : (shownKind === 'identified' || shownKind === 'pillars') ? 2
        : shownKind === 'market' ? 3 : 0;
    const maybeFlipChip = () => {
        if (Date.now() - lastChipFlip < CHIP_FLIP_MS) return;
        const ph = curPhase();
        for (let i = 0; i < CHIP_ORDER.length; i++) {
            const id = CHIP_ORDER[i];
            if (chipShown[id] || chipTarget.indexOf(id) === -1 || CHIP_PHASE[id] > ph) continue;
            chipShown[id] = true;
            lastChipFlip = Date.now();
            const el = panel.querySelector('[data-chip="' + id + '"]');
            if (el) {
                el.style.color = '#4ade80';
                el.style.background = 'rgba(34,197,94,0.12)';
                el.style.animation = 'glmChipIn .38s ease both';
                el.textContent = '✓ ' + el.getAttribute('data-label');
            }
            break;
        }
    };
    // Terminal state: a grade that finishes with flips still queued must not
    // leave amber chips behind — fill whatever is left without pacing.
    const fillAllChips = () => {
        for (let i = 0; i < CHIP_ORDER.length; i++) {
            const id = CHIP_ORDER[i];
            if (chipShown[id]) continue;
            chipShown[id] = true;
            const el = panel.querySelector('[data-chip="' + id + '"]');
            if (el) {
                el.style.color = '#4ade80';
                el.style.background = 'rgba(34,197,94,0.12)';
                el.style.animation = 'glmChipIn .38s ease both';
                el.textContent = '✓ ' + el.getAttribute('data-label');
            }
        }
    };
    let chipTimer = null;
    const startChipTimer = () => { if (!chipTimer) chipTimer = setInterval(maybeFlipChip, 350); };
    startChipTimer();
    // Pace the visible transitions — the real milestones all land inside the
    // first half of the grade while the comps tail runs, so shown raw the
    // first chips whiz by and "Market data" looks hung. Each stage keeps the
    // screen for at least this long before the next may replace it; the tick
    // always renders the freshest payload once the hold elapses, so nothing
    // shown is stale — it just isn't rushed past the eye.
    const PACE_MIN_VISIBLE = { text: 9000, detected: 14000, identified: 10000 };
    // Wait heartbeat on its OWN timer. It used to ride the progress poll,
    // which stop()s itself on a terminal grade-status verdict after 30s —
    // and grade-status answers 'done, grade:null' whenever the lock probe
    // reads free with no row yet (u4924, 2026-10-25: 82s grade, heartbeat
    // died after 15s; 2 of 11 long grades lost their 45s mark). The
    // heartbeat only ends when the modal is torn down (hideGradingModal /
    // completion hold), so the 15/45/90s marks reflect how long the user
    // actually sat in the modal.
    let heartbeatTimer = setInterval(function () {
        try {
            var es = Math.floor((Date.now() - submitStartMs) / 1000);
            while (waitMark < WAIT_MARKS.length && es >= WAIT_MARKS[waitMark]) {
                pgcGradeEvent('grade_wait', { elapsed_s: WAIT_MARKS[waitMark], stage: shownKind || (seenInFlight ? 'inflight' : 'pending'), poll: progressTimer ? 1 : 0 });
                waitMark++;
            }
            if (waitMark >= WAIT_MARKS.length) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
        } catch (e) {}
    }, 1000);
    let stopReason = null;
    const stop = (why) => {
        if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
        if (chipTimer) { clearInterval(chipTimer); chipTimer = null; }
        if (why && stopReason === null) {
            stopReason = why;
            // Visibility into the premature-terminal class: which verdict
            // stopped the chips, and how far into the grade.
            pgcGradeEvent('grade_poll_stop', { why: why, elapsed_s: Math.floor((Date.now() - submitStartMs) / 1000), seen_inflight: seenInFlight ? 1 : 0 });
        }
    };
    const teardown = () => {
        stop('teardown');
        if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
    };
    modal._stopGradeProgressPoll = teardown;
    // The completion hold stops the poll before it may have observed 'done'
    // — let it fill the queued chips first so the hold isn't amber.
    modal._fillGradeChips = fillAllChips;
    // A suspended/discarded tab kills the interval but keeps the handle — a
    // plain "if running, skip" check can't tell. Force a clean restart on
    // wake; the seenInFlight latch keeps a stale terminal verdict harmless.
    // The chip timer must restart too — killing it here used to freeze the
    // milestone chips amber for the rest of the grade.
    modal._restartGradeProgressPoll = function () {
        stop();
        progressTimer = setInterval(tick, 3000);
        startChipTimer();
        tick();
    };
    try {
        pgcGlmBeamMount(modal);
    } catch (e) {
        try {
            if (typeof logFunnelEvent === 'function') {
                logFunnelEvent('grade_beam_err', { where: 'mount', m: String((e && e.message) || e).slice(0, 140) });
            }
        } catch (e2) {}
    }

    const tick = async () => {
        try {
            if (document.hidden) return; // progress is only for eyes on the modal
            const res = await pgcGradeStatusGet(sinceSec, 'demo-progress');
            if (!res.ok) return;
            const st = res.body;
            if (!st || !st.success) return;
            // Deferred delivery: the recovery poll owns the modal close + panel.
            if (st.status === 'deferred') { stop('deferred'); return; }
            if (st.status === 'done' || st.status === 'failed') {
                // The first tick races the submit POST — grade-status can
                // answer 'done' before the job spec/lock even exists. Only a
                // terminal verdict is honored once the job has been seen
                // in-flight or enough time has passed for it to register —
                // and only THEN may the chips be force-filled, or a stale
                // 'done' paints all four green on the first tick.
                if (seenInFlight || Date.now() - submitStartMs > 30000) {
                    // 'done' with no grade = lock read free, nothing persisted.
                    // Mid-grade that is a false terminal (the grade request
                    // holds the lock; a probe miss shouldn't end the chips) —
                    // only a real envelope or a failure may stop the poll.
                    if (st.status === 'done' && !st.grade) {
                        if (!seenInFlight) stop('done_null_never_inflight'); // nothing ever ran — give up
                        return; // keep polling: the row will land
                    }
                    if (st.status === 'done') fillAllChips();
                    stop(st.status === 'done' ? 'done' : 'failed');
                }
                return;
            }
            seenInFlight = true;
            const prog = st.progress;
            if (!prog || !prog.stage) return;
            // Honest provider state: the hedge fired because the AI provider's
            // queue is backed up — say so instead of rotating generic copy.
            if (prog.stage === 'provider_busy' && shownKind === null) {
                const stB = modal.querySelector('#glm-status');
                if (stB) {
                    stB.style.opacity = '1';
                    stB.textContent = pgcT('glm.provider_busy', 'Our AI grading provider is busy right now — switching your card to the fast lane…');
                }
                modal._providerBusy = true;
                return;
            }
            // Dwell gate — an incoming stage may only replace what is on
            // screen once the current one has had its PACE_MIN_VISIBLE run.
            // 'market' bypasses: it is real data arriving near the end, and
            // holding it just risks the reveal landing first. A skipped stage
            // is simply superseded by the next tick's fresher payload.
            if (shownKind !== null && prog.stage !== 'market'
                && Date.now() - shownAt < (PACE_MIN_VISIBLE[shownKind] || 8000)) return;
            renderProg(prog);
        } catch (e) { /* poll hiccup — next tick retries */ }
    };

    function renderProg(prog) {
            // Beam overlay tracks the paced display (not raw server state) so
            // the annotations and the chips always agree.
            if (typeof modal._beamStage === 'function') modal._beamStage(prog.stage);
            const showName = prog.full_name || prog.name || '';
            // Card-bearing stages: 'detected' = Gemini's fast preliminary read
            // (~13s in), 'identified' = arbiter-settled, 'market' = comps pool
            // final. prog.done lists completed milestones — the chips render
            // from it, so they flip as real pipeline work finishes instead of
            // sitting static.
            const isCardStage = (prog.stage === 'detected' || prog.stage === 'identified'
                || prog.stage === 'pillars' || prog.stage === 'market');
            if (isCardStage) {
                if (showName) {
                    const parts = [];
                    if (prog.set_name) parts.push(prog.set_name);
                    if (prog.year) parts.push(prog.year);
                    if (prog.card_number) parts.push('#' + prog.card_number);
                    lastCard = { name: showName, sub: parts.length ? ' · ' + parts.join(' · ') : '' };
                }
                if (!lastCard) return;
                const done = Array.isArray(prog.done) ? prog.done : (
                    prog.stage === 'market' ? ['detected','identified','pillars','market']
                    : prog.stage === 'pillars' ? ['detected','identified','pillars']
                    : prog.stage === 'identified' ? ['detected','identified']
                    : ['detected']);
                // Display state, not payload state — the flip timer walks
                // this target one chip at a time.
                for (let i = 0; i < done.length; i++) {
                    if (chipTarget.indexOf(done[i]) === -1) chipTarget.push(done[i]);
                }
                const key = prog.stage + '|' + lastCard.name + '|' + done.length;
                if (key === shownKey) return;
                shownKey = key;
                shownKind = prog.stage;
                shownAt = Date.now();
                // The panel IS the status now — hide the redundant status line
                // and tip box so the added height can't scroll the card
                // preview above the fold.
                const stEl = modal.querySelector('#glm-status');
                if (stEl) stEl.style.display = 'none';
                const factEl = modal.querySelector('#glm-fact');
                if (factEl) factEl.style.display = 'none';
                const prevEl = modal.querySelector('.glm-preview');
                if (prevEl) prevEl.style.marginBottom = '14px';
                const upEl = modal.querySelector('#pgc-upload-prog');
                if (upEl) upEl.style.display = 'none';
                const verifying = prog.stage === 'detected' && done.indexOf('identified') === -1;
                const h2 = modal.querySelector('#glm-content h2');
                if (h2) {
                    h2.textContent = verifying
                        ? 'Card detected — verifying…'
                        : pgcT('gm.card.id', 'Card identified!');
                }
                const headColor = verifying ? '#60a5fa' : '#4ade80';
                const headBg = verifying ? 'rgba(59,130,246,0.10)' : 'rgba(34,197,94,0.10)';
                const headBorder = verifying ? 'rgba(59,130,246,0.25)' : 'rgba(34,197,94,0.25)';
                // The first milestone the server has NOT completed is the
                // stage actually running — it pulses amber so a pending chip
                // reads as live work instead of a stuck spinner. chipTarget
                // (server truth) is the source, not chipShown (paced display):
                // a chip queued for its flip beat must not glow, and during
                // the comps tail 'market' is the sole pulsing chip.
                const activeChipId = CHIP_ORDER.find(function (id) { return chipTarget.indexOf(id) === -1; });
                const chip = function (id, label) {
                    const on = !!chipShown[id];
                    const active = !on && id === activeChipId;
                    return '<span data-chip="' + id + '" data-label="' + label + '" style="font-size:11px;font-weight:600;'
                        + (on ? 'color:#4ade80;background:rgba(34,197,94,0.12);' : 'color:#fbbf24;background:rgba(251,191,36,0.14);')
                        + 'padding:3px 8px;border-radius:999px;'
                        + (active ? 'animation:glmChipPulse 1.4s ease-in-out infinite;' : '') + '">'
                        + (on ? '✓ ' : '◌ ') + label + '</span>';
                };
                // The pending phases get a "work in progress" line so a chip
                // sitting amber reads as the pipeline working, not the modal
                // hanging on a dead request.
                let workLine = '';
                if (prog.stage === 'market' && (prog.sold || prog.active)) {
                    const bits = [];
                    if (prog.sold) bits.push(prog.sold + ' sold');
                    if (prog.active) bits.push(prog.active + ' active');
                    if (bits.length) workLine = '<div style="color:#94a3b8;font-size:12px;margin-top:3px;">'
                        + escHtml('Market: ' + bits.join(' · ') + ' comps') + '</div>';
                } else if (done.indexOf('pillars') !== -1) {
                    workLine = '<div style="color:#94a3b8;font-size:12px;margin-top:3px;">'
                        + 'Scanning sold &amp; live market comps…</div>';
                } else {
                    workLine = '<div style="color:#94a3b8;font-size:12px;margin-top:3px;">'
                        + 'Measuring centering, edges, corners &amp; surface…</div>';
                }
                panel.innerHTML =
                    '<div style="background:' + headBg + ';border:1px solid ' + headBorder + ';border-radius:12px;padding:9px 12px;">'
                    + '<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:' + headColor + ';margin-bottom:4px;">'
                    + (verifying ? pgcT('gm.verifying', '◌ Card detected — verifying') : '✓ Card identified') + '</div>'
                    + '<div style="color:#ffffff;font-size:14px;font-weight:800;line-height:1.3;">' + escHtml(lastCard.name) + '</div>'
                    + (lastCard.sub ? '<div style="color:#94a3b8;font-size:12px;margin-top:2px;">' + escHtml(lastCard.sub) + '</div>' : '')
                    + workLine
                    + '<div style="display:flex;gap:5px;margin-top:8px;flex-wrap:wrap;">'
                    + chip('detected', 'Detected')
                    + chip('identified', 'Identified')
                    + chip('pillars', 'Grading pillars')
                    + chip('market', 'Market data')
                    + '</div></div>';
            } else if (!isCardStage && shownKey === null) {
                // Still pre-identification — surface the current stage (but
                // never downgrade a card panel back to a text stage).
                panel.innerHTML = '<div style="color:#94a3b8;font-size:12px;line-height:1.6;">'
                    + '<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#3b82f6;margin-right:8px;animation:glmPulse 1.2s ease-in-out infinite;"></span>'
                    + escHtml('Stage: ' + prog.stage) + '</div>';
                shownKind = 'text';
                shownAt = Date.now();
            }
            // Panel + hidden stock elements changed the height — re-fit so
            // the modal re-zooms instead of growing a scrollbar that pushes
            // the preview (and its brackets) out of view.
            if (typeof modal._fitCard === 'function') modal._fitCard();
    }
    progressTimer = setInterval(tick, 3000);
    tick();
}

// ── Blueprint-scan overlay on the card preview ──
// Ported from tmp/glm-demos/beam.js, but phases follow the real progress
// milestones instead of the lab's countdown: a repeating sweep while the
// pipeline analyzes, corner brackets once the card is detected, edge ticks +
// a centering cross when the pillars settle (the identified write only fires
// after centering merges, so the cross is truthful), green when the comps
// pool lands. Pure decoration — the progress panel owns all status text.
const PGC_BEAM_PHASE = { detected: 1, pillars: 2, identified: 2, market: 3 };

function pgcGlmBeamMount(modal) {
    // rm is logged for diagnostics only — the flag only gates homepage/
    // Lighthouse animation, not this overlay; always animate here.
    const reducedMotion = !!(window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const preview = modal && modal.querySelector('.glm-preview');
    const img = modal && modal.querySelector('#glm-card-image');
    if (!preview || !img) return;
    // Outside-edge annotations draw past the card bounds — safe to unclip:
    // the img is exactly 100%×100%, nothing inside relied on the clip.
    preview.style.overflow = 'visible';
    const cv = document.createElement('canvas');
    cv.setAttribute('aria-hidden', 'true');
    // max-width/height:none — style.css has a global `canvas { max-width:100% }`
    // reset that clamped the overlay to the preview width and left the sweep
    // ~24px short of the card's right edge in production.
    cv.style.cssText = 'position:absolute;inset:-12px;width:calc(100% + 24px);height:calc(100% + 24px);max-width:none;max-height:none;pointer-events:none;z-index:3;';
    preview.appendChild(cv);
    const g = cv.getContext('2d');
    // Diagnostic beacon — tells us which path a real browser takes when the
    // overlay fails to appear (reduced-motion, dead ctx, or never mounted).
    if (typeof pgcGradeEvent === 'function') {
        pgcGradeEvent('grade_beam_mount', { rm: reducedMotion ? 1 : 0, ctx: g ? 1 : 0 });
    }
    if (!g) { cv.remove(); return; }

    let dead = false, R = null, SW = 0, SH = 0;
    let phase = 0, beamLogged = false;
    const phaseAt = { 1: 0, 2: 0, 3: 0 };
    const scanStart = performance.now();

    function fit() {
        // Demo sizing: the scan needs room to read — ~200px card vs the stock
        // 120px thumbnail. fit() re-asserts on img load so the previewImg
        // onload landscape flip can't shrink it back.
        const landscape = img.naturalWidth > 0 && img.naturalWidth > img.naturalHeight;
        preview.style.width = landscape ? '272px' : '194px';
        preview.style.height = landscape ? '194px' : '272px';
        // fitCard measured before this resize — re-run it so the taller
        // preview re-zooms instead of overflowing the viewport.
        if (typeof modal._fitCard === 'function') modal._fitCard();
        // Size the buffer from the canvas's OWN box — never subtract
        // getBoundingClientRect across elements: under CSS zoom (fitCard)
        // Blink can return mixed spaces and the sweep ended up ~10% short.
        SW = cv.clientWidth; SH = cv.clientHeight;
        if (SW < 4) return;
        cv.width = Math.round(SW * devicePixelRatio);
        cv.height = Math.round(SH * devicePixelRatio);
        // The canvas is inset -12px on every side of the preview and the img
        // is exactly 100%×100% of it — the image rect in canvas space is
        // deterministic, no cross-element measuring required.
        R = { x: 12, y: 12, w: SW - 24, h: SH - 24 };
    }
    img.addEventListener('load', fit);
    window.addEventListener('resize', fit);
    fit();

    const clamp01 = t => Math.max(0, Math.min(1, t));
    const easeOut = t => 1 - Math.pow(1 - clamp01(t), 3);
    const easeInOut = t => t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    function bracket(x, y, dx, dy, len, t, color) {
        // L bracket growing with t∈0..1: vertical leg first, then horizontal
        g.strokeStyle = color; g.lineWidth = 2.5; g.lineCap = 'square';
        g.shadowColor = color; g.shadowBlur = 5;
        g.beginPath();
        g.moveTo(x, y + dy * len * Math.min(t * 2, 1));
        g.lineTo(x, y);
        g.lineTo(x + dx * len * Math.max(t * 2 - 1, 0), y);
        g.stroke();
        g.shadowBlur = 0;
    }

    function draw(now) {
        if (!R) return;
        g.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
        const { x, y, w, h } = R;
        g.clearRect(0, 0, SW, SH);

        // faint survey grid inside the card bounds
        g.strokeStyle = 'rgba(148,163,184,.12)'; g.lineWidth = 1;
        for (let i = 1; i < 4; i++) {
            g.beginPath(); g.moveTo(x + w * i / 4, y); g.lineTo(x + w * i / 4, y + h); g.stroke();
            g.beginPath(); g.moveTo(x, y + h * i / 4); g.lineTo(x + w, y + h * i / 4); g.stroke();
        }

        // scanning pass — repeats while the pipeline is pre-detection.
        // Ping-pong: ~5.4s sweep down, a beat held on the bottom edge, the
        // same sweep back up, a beat on the top edge — an ~11.9s cycle of
        // slow flatbed-style motion, not a flicker or a snapping reset.
        if (phase === 0) {
            const LEG = 5400, HOLD = 650, CYC = LEG * 2 + HOLD * 2;
            const tt = (now - scanStart) % CYC;
            let e;
            if (tt < LEG) e = easeInOut(tt / LEG);                                    // sweep down
            else if (tt < LEG + HOLD) e = 1;                                          // hold bottom
            else if (tt < LEG * 2 + HOLD) e = easeInOut(1 - (tt - LEG - HOLD) / LEG); // sweep up
            else e = 0;                                                               // hold top
            const by = y + h * e;
            const grad = g.createLinearGradient(0, by - 26, 0, by + 26);
            grad.addColorStop(0, 'rgba(255,255,255,0)');
            grad.addColorStop(.5, 'rgba(255,255,255,.38)');
            grad.addColorStop(1, 'rgba(255,255,255,0)');
            g.fillStyle = grad;
            g.fillRect(x, by - 26, w, 52);
            g.fillStyle = 'rgba(255,255,255,.95)';
            g.fillRect(x, by - 1.5, w, 3);
        }

        const settled = phase >= 3;
        const bracketColor = settled ? '#22c55e' : '#fbbf24';
        const tickColor = settled ? 'rgba(74,222,128,.9)' : 'rgba(56,189,248,.9)';

        // corner brackets — once the card is detected; each corner fully
        // traces (~1.6s) BEFORE the next begins (~6.4s total) so they read
        // as four individual draws around the perimeter, not one
        // synchronized pop.
        if (phaseAt[1]) {
            const o = 6, bl = 19;
            [[x - o, y - o, 1, 1], [x + w + o, y - o, -1, 1],
             [x + w + o, y + h + o, -1, -1], [x - o, y + h + o, 1, -1]]
                .forEach((c, i) => {
                    const t = easeOut((now - phaseAt[1] - i * 1600) / 1600);
                    if (t > 0) bracket(c[0], c[1], c[2], c[3], bl, t, bracketColor);
                });
        }

        // (2026-11-02 owner: blue edge ticks removed.)

        // centering cross — once the pillars settle. Waits for the corner
        // brackets to finish tracing (phase 1: 4 corners × 1.6s = 6.4s) plus a
        // 500ms beat so it lands as a separate step.
        if (phaseAt[2]) {
            g.strokeStyle = tickColor; g.lineWidth = 1.5;
            const crossAt = phaseAt[1] ? Math.max(phaseAt[2] + 2200, phaseAt[1] + 6900) : phaseAt[2] + 2200;
            const ct = easeInOut(clamp01((now - crossAt) / 1100));
            g.setLineDash([6, 5]);
            const gx = ct * w, gy = ct * h;
            g.beginPath(); g.moveTo(x + w / 2, y + h / 2 - gy / 2); g.lineTo(x + w / 2, y + h / 2 + gy / 2); g.stroke();
            g.beginPath(); g.moveTo(x + w / 2 - gx / 2, y + h / 2); g.lineTo(x + w / 2 + gx / 2, y + h / 2); g.stroke();
            g.setLineDash([]);
        }
    }

    // Draw-loop instrumentation: a first exception kills the rAF chain and
    // leaves a blank canvas with zero signals — beacon it so a dead overlay
    // on a real browser is diagnosable instead of invisible.
    let frames = 0, errLogged = false;
    function frame(now) {
        if (dead || !cv.isConnected) return;
        try {
            if (!R) fit();  // a 0-rect race at mount must not wedge the overlay
            draw(now);
            if (++frames === 20 && typeof pgcGradeEvent === 'function') {
                pgcGradeEvent('grade_beam_draw', {
                    sw: Math.round(SW), sh: Math.round(SH),
                    rw: R ? Math.round(R.w) : 0, rh: R ? Math.round(R.h) : 0,
                    dpr: devicePixelRatio
                });
            }
        } catch (e) {
            if (!errLogged) {
                errLogged = true;
                try {
                    if (typeof logFunnelEvent === 'function') {
                        logFunnelEvent('grade_beam_err', { m: String((e && e.message) || e).slice(0, 140) });
                    }
                } catch (e2) {}
            }
            return;
        }
        requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    modal._beamStage = function (stage) {
        const idx = PGC_BEAM_PHASE[stage] || 0;
        if (idx <= phase) return;
        // Skipped levels get staggered starts — a paced render that jumps
        // detected→identified still shows corners tracing first, then ticks
        // + cross follow ~1.4s later instead of a one-frame pile-up.
        const t0 = performance.now();
        for (let k = phase + 1; k <= idx; k++) phaseAt[k] = t0 + (k - 1 - phase) * 1400;
        phase = idx;
        if (!beamLogged) {
            beamLogged = true;
            if (typeof logFunnelEvent === 'function') {
                logFunnelEvent('grade_beam_state', { r: R ? 1 : 0, sw: Math.round(SW), ph: phase });
            }
        }
    };

    // Hidden-tab replay: the browser pauses rAF while the tab is hidden, so a
    // stage that lands in the background reaches its final state unseen — when
    // the user returns, corners/ticks/cross look "just stamped on". If the
    // whole draw sequence finished while hidden (>5s), re-anchor the phase
    // timestamps so the user actually sees it trace from the start.
    let lastHiddenAt = 0;
    const onVisChange = function () {
        if (document.visibilityState === 'hidden') { lastHiddenAt = performance.now(); return; }
        if (!lastHiddenAt || phase <= 0) { lastHiddenAt = 0; return; }
        const hiddenFor = performance.now() - lastHiddenAt;
        lastHiddenAt = 0;
        if (hiddenFor > 5000) {
            const rt0 = performance.now();
            for (let k = 1; k <= phase; k++) phaseAt[k] = rt0 + (k - 1) * 1400;
        }
    };
    document.addEventListener('visibilitychange', onVisChange);

    modal._beamDestroy = function () {
        dead = true;
        document.removeEventListener('visibilitychange', onVisChange);
        img.removeEventListener('load', fit);
        window.removeEventListener('resize', fit);
        cv.remove();
    };
}

function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
}

function hideGradingModal() {
    // Stop the demo progress poller first — the modal is going away.
    const curModal = document.getElementById('grading-loading-modal');
    if (curModal && typeof curModal._beamDestroy === 'function') {
        curModal._beamDestroy();
    }
    if (curModal && typeof curModal._stopGradeProgressPoll === 'function') {
        curModal._stopGradeProgressPoll();
    }
    // Restore promo footer and sticky CTA
    document.querySelectorAll('.pgc-promo, #sticky-mini-cta').forEach(el => el.style.removeProperty('display'));

    const backToTop = document.querySelector('.back-to-top');
    if (backToTop) {
        backToTop.classList.remove('modal_hidden');
        backToTop.style.removeProperty('display');
    }
    const modal = document.getElementById('grading-loading-modal');
    if (!modal) return;
    const intervals = JSON.parse(modal.dataset.intervals || '{}');
    clearInterval(intervals.fact);
    clearInterval(intervals.status);
    clearInterval(intervals.countdown);

    const backdrop = modal.querySelector('.glm-backdrop');
    const card = modal.querySelector('.glm-card');
    const fallbackCover = modal.querySelector('.glm-preview-fallback');
    const previewImg = modal.querySelector('#glm-card-image');

    const objectUrl = modal.dataset.objectUrl;
    if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        delete modal.dataset.objectUrl;
    }

    if (previewImg) {
        previewImg.removeAttribute('src');
        previewImg.style.opacity = '0';
    }
    if (fallbackCover) {
        fallbackCover.style.opacity = '1';
    }

    card.style.transform = 'translateY(-16px) scale(0.98)';
    backdrop.style.opacity = '0';
    setTimeout(() => { modal.style.display = 'none'; }, 420);
}

function stopCountdown(modal) {
    if (!modal) return;
    const intervals = JSON.parse(modal.dataset.intervals || '{}');
    clearInterval(intervals.countdown);
}

function showGradeReveal(grade, condition, cardPreviewUrl, onComplete) {
    const modal = document.getElementById('grading-loading-modal');
    // Missing modal can't dead-end the flow — run the completion (hide +
    // scroll/report nav) or the user is left at whatever scroll offset the
    // form happened to sit at.
    if (!modal) { if (typeof onComplete === 'function') onComplete(); return; }

    const intervals = JSON.parse(modal.dataset.intervals || '{}');
    clearInterval(intervals.fact);
    clearInterval(intervals.status);
    clearInterval(intervals.countdown);

    // Hold on the fully-annotated card so the finished scan state is visible
    // before the reveal replaces it. A single click anywhere re-enters this
    // function with the hold flag set.
    if (!modal.dataset.demoHold) {
        modal.dataset.demoHold = '1';
        if (typeof modal._fillGradeChips === 'function') modal._fillGradeChips();
        if (typeof modal._stopGradeProgressPoll === 'function') modal._stopGradeProgressPoll();
        const h2h = modal.querySelector('#glm-content h2');
        if (h2h) { h2h.textContent = 'Grade complete'; h2h.style.color = '#4ade80'; }
        const cwh = modal.querySelector('#glm-countdown-wrap');
        if (cwh) cwh.style.display = 'none';
        const panelh = modal.querySelector('#glm-progress');
        if (panelh) {
            panelh.style.display = 'block';
            panelh.style.textAlign = 'center';
            panelh.innerHTML =
                '<div style="color:#4ade80;font-size:13px;font-weight:700;margin-bottom:10px;">Scan complete — annotations locked in</div>'
                + '<span style="display:inline-block;background:#22c55e;color:#052e16;font-weight:800;font-size:14px;padding:11px 26px;border-radius:999px;">Show my grade →</span>'
                + '<div style="color:#64748b;font-size:11px;margin-top:8px;">click anywhere to continue</div>';
        }
        const cont = () => {
            modal.removeEventListener('click', cont);
            showGradeReveal(grade, condition, cardPreviewUrl, onComplete);
        };
        modal.addEventListener('click', cont);
        if (typeof modal._fitCard === 'function') modal._fitCard();
        return;
    }

    const theme = getGradeTheme(grade);
    const gradeStr = grade != null ? String(grade) : 'N/A';
    const card = modal.querySelector('.glm-card');
    let content = modal.querySelector('#glm-content');
    if (!content) { content = card; }
    if (!content) { return; }

    card.style.background = theme.bgGradient;
    card.style.border = '1px solid ' + theme.color + '44';
    card.style.boxShadow = '0 25px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08)';
    card.style.transition = 'background 0.6s ease, border 0.6s ease, box-shadow 0.6s ease';

    const confettiContainer = document.createElement('div');
    confettiContainer.style.cssText = 'position:absolute; inset:0; overflow:hidden; pointer-events:none; z-index:10;';

    content.innerHTML = '';
    const revealWrap = document.createElement('div');
    revealWrap.style.cssText = 'position: relative; overflow: hidden; border-radius: 16px; padding: 8px 0;';
    revealWrap.appendChild(confettiContainer);

    const inner = document.createElement('div');
    inner.style.cssText = 'position: relative; z-index: 5;';
    revealWrap.appendChild(inner);

    const previewDiv = document.createElement('div');
    previewDiv.style.cssText = 'width:90px; height:126px; margin:0 auto 18px; border-radius:12px; position:relative; overflow:hidden; box-shadow:0 15px 35px rgba(0,0,0,0.4), 0 0 0 1px ' + theme.color + '55; opacity:0; transform:scale(0.6);';
    if (cardPreviewUrl) {
        previewDiv.innerHTML = '<img src="' + cardPreviewUrl + '" alt="Your card" style="width:100%; height:100%; object-fit:cover; border-radius:inherit;">';
        const revealPreviewImage = previewDiv.querySelector('img');
        revealPreviewImage.onload = () => {
            const isLandscape = revealPreviewImage.naturalWidth > revealPreviewImage.naturalHeight;
            previewDiv.style.width = isLandscape ? '126px' : '90px';
            previewDiv.style.height = isLandscape ? '90px' : '126px';
        };
    } else {
        previewDiv.innerHTML = '<div style="width:100%; height:100%; background:linear-gradient(135deg,' + theme.color + ' 0%,#8b5cf6 100%); display:flex; align-items:center; justify-content:center;"><span style="font-size:28px;">' + theme.icon + '</span></div>';
    }
    inner.appendChild(previewDiv);

    const scanLines = [];
    for (let s = 0; s < 3; s++) {
        const sl = document.createElement('div');
        sl.style.cssText = 'position:absolute; left:0; right:0; height:' + (3 - s) + 'px; background:linear-gradient(90deg, transparent, ' + theme.color + ', transparent); box-shadow:0 0 14px ' + theme.color + '; top:0; pointer-events:none; opacity:0;';
        previewDiv.appendChild(sl);
        scanLines.push(sl);
    }

    const statusDiv = document.createElement('div');
    statusDiv.style.cssText = 'font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:0.12em; color:#94a3b8; margin-bottom:10px; opacity:0; min-height:20px;';
    statusDiv.textContent = 'Analysis Complete';
    inner.appendChild(statusDiv);

    const stage = document.createElement('div');
    stage.style.cssText = 'position:relative; margin:2px auto 12px; width:200px; height:200px; display:flex; align-items:center; justify-content:center;';

    const scoreBezel = document.createElement('div');
    scoreBezel.style.cssText = 'position:absolute; inset:24px; border:3px solid ' + theme.color + '; border-radius:50%; background:rgba(15,23,42,0.18); box-shadow:inset 0 0 24px rgba(255,255,255,0.05); opacity:0; transform:scale(0.7);';
    const orbitRing = document.createElement('div');
    orbitRing.style.cssText = 'position:absolute; inset:6px; border:2px dashed ' + theme.color + '88; border-radius:50%; opacity:0; transform:scale(0.7);';
    const orbitRingInner = document.createElement('div');
    orbitRingInner.style.cssText = 'position:absolute; inset:12px; border:1px solid ' + theme.color + '55; border-radius:50%; opacity:0; transform:scale(0.7);';
    stage.appendChild(orbitRing);
    stage.appendChild(orbitRingInner);
    stage.appendChild(scoreBezel);

    const numWrap = document.createElement('div');
    numWrap.style.cssText = 'position:relative; display:flex; align-items:flex-start; justify-content:center; opacity:0; transform:scale(0.7);';

    const gradeNumEl = document.createElement('div');
    gradeNumEl.id = 'glm-grade-num';
    gradeNumEl.style.cssText = 'font-size:96px; font-weight:900; color:#ffffff; line-height:1; text-shadow:0 0 6px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.5); font-variant-numeric:tabular-nums; opacity:0; transition: color 0.2s, text-shadow 0.2s;';
    gradeNumEl.textContent = '';
    const renderGradeScore = value => {
        gradeNumEl.innerHTML = '<span>' + value + '</span><small style="font-size:18px; font-weight:700; margin-left:4px; vertical-align:18px; opacity:0.9;">/10</small>';
    };

    numWrap.appendChild(gradeNumEl);
    stage.appendChild(numWrap);
    inner.appendChild(stage);

    const badgeDiv = document.createElement('div');
    badgeDiv.style.cssText = 'display:inline-flex; align-items:center; gap:8px; background:' + theme.color + '22; border:1px solid ' + theme.color + '66; border-radius:999px; padding:8px 22px; margin-bottom:14px; opacity:0;';
    badgeDiv.innerHTML = '<span style="font-size:18px;">' + theme.icon + '</span><span style="font-size:15px; font-weight:800; color:' + theme.color + '; text-transform:uppercase; letter-spacing:0.05em;">' + (condition || theme.label) + '</span>';
    inner.appendChild(badgeDiv);

    const continueBtn = document.createElement('button');
    continueBtn.id = 'glm-continue-btn';
    continueBtn.style.cssText = 'width:100%; padding:16px 32px; border-radius:14px; border:none; background:linear-gradient(135deg, ' + theme.color + ', ' + theme.color + 'dd); color:white; font-size:16px; font-weight:800; cursor:pointer; box-shadow:0 10px 25px rgba(0,0,0,0.4); opacity:0; letter-spacing:0.02em;';
    continueBtn.textContent = 'Continue to Full Report \u2192';
    inner.appendChild(continueBtn);

    const subtitleDiv = document.createElement('div');
    subtitleDiv.style.cssText = 'margin-top:12px; font-size:12px; color:#64748b; opacity:0;';
    subtitleDiv.textContent = 'Sub-grades \u00b7 Centering \u00b7 Market Data \u00b7 Visual Overlay';
    inner.appendChild(subtitleDiv);

    content.appendChild(revealWrap);
    if (modal._fitCard) { modal._fitCard(); requestAnimationFrame(modal._fitCard); }

    previewDiv.animate([
        { transform: 'scale(0.6) translateY(10px)', opacity: 0, filter: 'blur(8px)' },
        { transform: 'scale(1) translateY(0)', opacity: 1, filter: 'blur(0px)' }
    ], { duration: 1000, easing: 'ease-out', fill: 'forwards' });

    previewDiv.animate([
        { transform: 'translateY(0) scale(1)' },
        { transform: 'translateY(-4px) scale(1.02)' },
        { transform: 'translateY(0) scale(1)' }
    ], { duration: 1500, delay: 700, iterations: 5, easing: 'ease-in-out' });

    scanLines.forEach((sl, i) => {
        sl.animate([
            { top: '0%', opacity: 0 },
            { top: '12%', opacity: 0.7, offset: 0.1 },
            { top: '88%', opacity: 0.7, offset: 0.9 },
            { top: '100%', opacity: 0 }
        ], { duration: 900, delay: 200 + i * 350, easing: 'linear', fill: 'forwards' });
    });

    statusDiv.animate([
        { transform: 'translateY(15px)', opacity: 0 },
        { transform: 'translateY(0)', opacity: 1 }
    ], { duration: 500, delay: 800, easing: 'ease-out', fill: 'forwards' });

    const statusMessages = [
        { t: 500, txt: 'Initializing Grading Matrix...', size: '13px' },
        { t: 1000, txt: 'Analyzing Surface & Corners...', size: '13px' },
        { t: 1600, txt: 'Calculating Centering Ratio...', size: '13px' },
        { t: 1800, txt: 'Finalizing Score...', size: '14px' },
        { t: 2500, txt: 'Revealing Your Grade!', size: '17px' }
    ];
    statusMessages.forEach(m => {
        setTimeout(() => {
            statusDiv.textContent = m.txt;
            statusDiv.style.fontSize = m.size;
            statusDiv.style.color = m.t >= 12000 ? '#ffffff' : theme.color;
            statusDiv.style.letterSpacing = m.t >= 12000 ? '0.06em' : '0.12em';
        }, m.t);
    });

    scoreBezel.animate([
        { opacity: 0, transform: 'scale(0.7)' },
        { opacity: 1, transform: 'scale(1)' }
    ], { duration: 400, delay: 700, easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', fill: 'forwards' });
    orbitRing.animate([
        { opacity: 0, transform: 'scale(0.65) rotate(0deg)' },
        { opacity: 0.85, transform: 'scale(1) rotate(0deg)', offset: 0.12 },
        { opacity: 0.85, transform: 'scale(1) rotate(720deg)' }
    ], { duration: 3000, delay: 700, easing: 'linear', fill: 'forwards' });
    orbitRingInner.animate([
        { opacity: 0, transform: 'scale(0.65) rotate(0deg)' },
        { opacity: 0.7, transform: 'scale(1) rotate(0deg)', offset: 0.12 },
        { opacity: 0.7, transform: 'scale(1) rotate(-480deg)' }
    ], { duration: 3000, delay: 800, easing: 'linear', fill: 'forwards' });

    numWrap.animate([
        { opacity: 0, transform: 'scale(0.6)' },
        { opacity: 1, transform: 'scale(1)' }
    ], { duration: 350, delay: 800, easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', fill: 'forwards' });

    gradeNumEl.animate([
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 300, delay: 850, easing: 'ease-out', fill: 'forwards' });

    if (grade != null && !isNaN(parseFloat(grade))) {
        const finalGrade = parseFloat(grade);
        const slotStart = 900;
        const slotDuration = 1800;
        const slotStartTime = performance.now() + slotStart;
        let lastUpdate = 0;

        function slotStep(now) {
            const elapsed = now - slotStartTime;
            if (elapsed < 0) { requestAnimationFrame(slotStep); return; }
            const progress = Math.min(elapsed / slotDuration, 1);

            if (progress >= 1) {
                renderGradeScore(gradeStr);
                gradeNumEl.style.color = theme.color;
                gradeNumEl.style.textShadow = '0 0 4px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.5)';
                gradeNumEl.style.filter = 'blur(0px)';
                return;
            }

            let eased;
            if (progress < 0.25) {
                eased = progress * 0.15;
            } else if (progress < 0.55) {
                eased = 0.0375 + (progress - 0.25) * 0.6;
            } else {
                const phaseProgress = (progress - 0.55) / 0.45;
                eased = 0.2175 + (1 - Math.pow(1 - phaseProgress, 3.5)) * 0.7825;
            }

            const range = (1 - eased) * 10;
            const randomVal = finalGrade + (Math.random() - 0.5) * range;
            const displayVal = Math.max(0, Math.min(10, randomVal));
            const updateInterval = 60 + progress * 440;

            if (now - lastUpdate >= updateInterval) {
                const scoreText = Number.isInteger(finalGrade) ? Math.round(displayVal) : displayVal.toFixed(1);
                renderGradeScore(scoreText);
                gradeNumEl.style.color = '#ffffff';
                gradeNumEl.style.textShadow = '0 0 6px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.5)';
                if (progress < 0.35) { gradeNumEl.style.filter = 'blur(1.8px)'; }
                else if (progress < 0.7) { gradeNumEl.style.filter = 'blur(0.8px)'; }
                else { gradeNumEl.style.filter = 'blur(0px)'; }
                gradeNumEl.animate([
                    { transform: 'scale(1.05)' },
                    { transform: 'scale(1)' }
                ], { duration: 90, easing: 'ease-out', fill: 'forwards' });
                lastUpdate = now;
            }
            requestAnimationFrame(slotStep);
        }
        requestAnimationFrame(slotStep);
    }

    const flashOverlay = document.createElement('div');
    flashOverlay.style.cssText = 'position:absolute; inset:0; background:white; border-radius:16px; pointer-events:none; z-index:20; opacity:0;';
    revealWrap.appendChild(flashOverlay);
    flashOverlay.animate([
        { opacity: 0 },
        { opacity: 0.9, offset: 0.06 },
        { opacity: 0.5, offset: 0.2 },
        { opacity: 0.85, offset: 0.4 },
        { opacity: 0.2, offset: 0.65 },
        { opacity: 0 }
    ], { duration: 700, delay: 2700, easing: 'ease-out', fill: 'forwards' });

    scoreBezel.animate([
        { borderColor: theme.color, boxShadow: 'inset 0 0 24px rgba(255,255,255,0.05)' },
        { borderColor: '#ffffff', boxShadow: 'inset 0 0 40px ' + theme.color + '55, 0 0 24px ' + theme.color + '66', offset: 0.25 },
        { borderColor: theme.color, boxShadow: 'inset 0 0 24px rgba(255,255,255,0.05)' }
    ], { duration: 800, delay: 2700, easing: 'ease-out', fill: 'forwards' });
    numWrap.animate([
        { transform: 'scale(1) rotate(0)', opacity: 1 },
        { transform: 'scale(0.65) rotate(-18deg)', opacity: 0.25, offset: 0.05 },
        { transform: 'scale(1.5) rotate(10deg)', opacity: 1, offset: 0.25 },
        { transform: 'scale(0.9) rotate(-4deg)', offset: 0.45 },
        { transform: 'scale(1.12) rotate(2deg)', offset: 0.65 },
        { transform: 'scale(0.98) rotate(0)', offset: 0.82 },
        { transform: 'scale(1) rotate(0)', opacity: 1 }
    ], { duration: 900, delay: 2700, easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', fill: 'forwards' });

    for (let i = 0; i < 50; i++) {
        const sparkle = document.createElement('div');
        const angle = (i / 50) * Math.PI * 2 + Math.random() * 0.4;
        const dist = 130 + Math.random() * 100;
        const sx = Math.cos(angle) * dist;
        const sy = Math.sin(angle) * dist;
        const sparkleSize = 4 + Math.random() * 10;
        const sparkleColor = theme.rainbow
            ? ['#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#a3e635', '#f472b6', '#ef4444'][Math.floor(Math.random() * 10)]
            : theme.color;
        sparkle.style.cssText = 'position:absolute; left:50%; top:45%; width:' + sparkleSize + 'px; height:' + sparkleSize + 'px; border-radius:50%; background:' + sparkleColor + '; pointer-events:none; z-index:3; opacity:0; transform:translate(-50%, -50%) scale(0);';
        revealWrap.appendChild(sparkle);
        sparkle.animate([
            { transform: 'translate(-50%, -50%) scale(0)', opacity: 0 },
            { transform: 'translate(calc(-50% + ' + sx * 0.2 + 'px), calc(-50% + ' + sy * 0.2 + 'px)) scale(3.5)', opacity: 1, offset: 0.15 },
            { transform: 'translate(calc(-50% + ' + sx + 'px), calc(-50% + ' + sy + 'px)) scale(0)', opacity: 0 }
        ], { duration: 1500 + Math.random() * 600, delay: 5000 + Math.random() * 400, easing: 'ease-out', fill: 'forwards' });
    }

    numWrap.animate([
        { transform: 'translateX(0)' },
        { transform: 'translateX(-14px)' },
        { transform: 'translateX(11px)' },
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(6px)' },
        { transform: 'translateX(-3px)' },
        { transform: 'translateX(2px)' },
        { transform: 'translateX(0)' }
    ], { duration: 500, delay: 3500, easing: 'ease-out', fill: 'forwards' });

    numWrap.animate([
        { transform: 'scale(1)' },
        { transform: 'scale(1.06)' },
        { transform: 'scale(1)' }
    ], { duration: 1200, delay: 3900, iterations: Infinity, easing: 'ease-in-out' });

    const shineSweep = document.createElement('div');
    shineSweep.style.cssText = 'position:absolute; inset:-20px; overflow:hidden; pointer-events:none; z-index:4;';
    const shineBar = document.createElement('div');
    shineBar.style.cssText = 'position:absolute; top:-50%; left:-100%; width:50%; height:200%; background:linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent); transform:rotate(25deg);';
    shineSweep.appendChild(shineBar);
    numWrap.appendChild(shineSweep);
    shineBar.animate([{ left: '-100%' }, { left: '200%' }], { duration: 600, delay: 3200, easing: 'ease-in-out', fill: 'forwards' });
    shineBar.animate([{ left: '-100%' }, { left: '200%' }], { duration: 700, delay: 4200, easing: 'ease-in-out', fill: 'forwards' });

    badgeDiv.animate([
        { transform: 'translateY(30px) scale(0.7)', opacity: 0 },
        { transform: 'translateY(0) scale(1.2)', opacity: 1, offset: 0.5 },
        { transform: 'translateY(0) scale(0.93)', offset: 0.7 },
        { transform: 'translateY(0) scale(1.03)', offset: 0.85 },
        { transform: 'translateY(0) scale(1)', opacity: 1 }
    ], { duration: 550, delay: 3700, easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', fill: 'forwards' });

    continueBtn.animate([
        { transform: 'translateY(30px) scale(0.9)', opacity: 0 },
        { transform: 'translateY(0) scale(1)', opacity: 1 }
    ], { duration: 450, delay: 4100, easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', fill: 'forwards' });

    continueBtn.animate([
        { boxShadow: '0 10px 25px rgba(0,0,0,0.4)' },
        { boxShadow: '0 18px 40px rgba(0,0,0,0.5)' },
        { boxShadow: '0 10px 25px rgba(0,0,0,0.4)' }
    ], { duration: 1200, delay: 4500, iterations: Infinity, easing: 'ease-in-out' });

    subtitleDiv.animate([
        { transform: 'translateY(20px)', opacity: 0 },
        { transform: 'translateY(0)', opacity: 1 }
    ], { duration: 350, delay: 4200, easing: 'ease-out', fill: 'forwards' });

    const confettiCount = theme.confettiCount || 30;
    const confettiColors = theme.rainbow
        ? ['#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444', '#06b6d4', '#a3e635', '#f472b6']
        : [theme.color, '#fbbf24', '#ffffff', theme.color, '#f59e0b'];

    function spawnConfetti(amount, startDelay, durationMin, durationMax, delaySpread) {
        for (let i = 0; i < amount; i++) {
            const piece = document.createElement('div');
            const size = 6 + Math.random() * 12;
            const color = confettiColors[Math.floor(Math.random() * confettiColors.length)];
            const shape = Math.random();
            const radius = shape > 0.6 ? '50%' : (shape > 0.3 ? '2px' : '0');
            piece.style.cssText = 'position:absolute; left:' + (Math.random() * 100) + '%; top:-20px; width:' + size + 'px; height:' + size + 'px; background:' + color + '; border-radius:' + radius + '; pointer-events:none;';
            confettiContainer.appendChild(piece);
            piece.animate([
                { transform: 'translateY(-100vh) rotate(0deg)', opacity: 1 },
                { transform: 'translateY(100vh) rotate(' + (360 + Math.random() * 1080) + 'deg)', opacity: 0 }
            ], { duration: durationMin + Math.random() * (durationMax - durationMin), delay: startDelay + Math.random() * delaySpread, easing: 'ease-in', fill: 'forwards' });
        }
    }

    spawnConfetti(confettiCount, 2700, 2000, 3500, 1200);
    spawnConfetti(Math.floor(confettiCount * 0.5), 4600, 1800, 3000, 1000);

    for (let i = 0; i < 28; i++) {
        const sparkle = document.createElement('div');
        const angle = Math.random() * Math.PI * 2;
        const dist = 70 + Math.random() * 110;
        const sx = Math.cos(angle) * dist;
        const sy = Math.sin(angle) * dist;
        const sparkleSize = 3 + Math.random() * 7;
        const sparkleColor = theme.rainbow ? confettiColors[i % confettiColors.length] : theme.color;
        sparkle.style.cssText = 'position:absolute; left:50%; top:45%; width:' + sparkleSize + 'px; height:' + sparkleSize + 'px; border-radius:50%; background:' + sparkleColor + '; pointer-events:none; z-index:3; opacity:0; transform:translate(-50%, -50%) scale(0);';
        revealWrap.appendChild(sparkle);
        sparkle.animate([
            { transform: 'translate(-50%, -50%) scale(0)', opacity: 0 },
            { transform: 'translate(calc(-50% + ' + sx * 0.2 + 'px), calc(-50% + ' + sy * 0.2 + 'px)) scale(2.3)', opacity: 1, offset: 0.25 },
            { transform: 'translate(calc(-50% + ' + sx + 'px), calc(-50% + ' + sy + 'px)) scale(0)', opacity: 0 }
        ], { duration: 1000 + Math.random() * 500, delay: 3800 + Math.random() * 500, easing: 'ease-out', fill: 'forwards' });
    }

    continueBtn.addEventListener('mouseenter', () => {
        continueBtn.style.transform = 'translateY(-2px) scale(1.02)';
        continueBtn.style.boxShadow = '0 18px 40px rgba(0,0,0,0.5)';
    });
    continueBtn.addEventListener('mouseleave', () => {
        continueBtn.style.transform = '';
        continueBtn.style.boxShadow = '0 10px 25px rgba(0,0,0,0.4)';
    });
    continueBtn.addEventListener('click', () => {
        // Notify promo-engine.js that the reveal is complete so it can fire
        // post_grade triggers and refresh bottom bars.
        window.dispatchEvent(new CustomEvent('pgc:gradeRevealComplete'));
        if (typeof onComplete === 'function') onComplete();
    });
}

function getRandomFact() {
    return GRADING_FACTS[Math.floor(Math.random() * GRADING_FACTS.length)];
}

// The ONE truth for "does this input have a photo": camera captures that hit
// a DataTransfer-less engine are staged on input._heicFile with EMPTY .files
// (card-scanner.js stages them that way by design), and the HEIC conversion
// path does the same. Every gate that read only .files called a staged photo
// "no photo" — the u4617-class dead end. All submit-path checks go through
// this helper.
function pgcStagedFile(input) {
    if (!input) return null;
    return input._heicFile || (input.files && input.files[0]) || null;
}

// Pulsing-ring + hint styles for the "needs a front photo" zone state; shared
// by the no-photo and back-only submit gates.
function pgcEnsureZoneHintCss() {
    if (window.__pgcZoneHintCssDone) return;
    window.__pgcZoneHintCssDone = true;
    const zst = document.createElement('style');
    zst.textContent =
        '@keyframes pgcZonePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.55); } 50% { box-shadow: 0 0 0 10px rgba(239,68,68,0); } }'
        + '.pgc-needs-photo{border-color:#ef4444 !important;background:#fef2f2 !important;animation:pgcZonePulse 1.1s ease-in-out 3;}'
        + '.pgc-zone-hint{display:flex;align-items:center;justify-content:center;gap:6px;margin-top:8px;font-size:13px;font-weight:800;color:#dc2626;background:#fee2e2;border:1px solid #fca5a5;border-radius:9px;padding:8px 12px;}';
    document.head.appendChild(zst);
}

// Move whatever is staged on `from` to `to` (both DataTransfer and the
// _heicFile stash), clearing `from`. Preview/zone classes are synced so the
// UI matches reality. Returns the moved file or null.
function pgcPromoteStagedFile(fromInput, toInput) {
    const file = pgcStagedFile(fromInput);
    if (!file || !toInput) return null;
    try {
        const dt = new DataTransfer();
        dt.items.add(file);
        toInput.files = dt.files;
    } catch (e) { /* _heicFile stash below covers DataTransfer-less engines */ }
    if (!toInput.files || !toInput.files.length || fromInput._heicFile) {
        toInput._heicFile = file;
    }
    // Clear the source slot (input + stash).
    try { fromInput.value = ''; } catch (e) {}
    fromInput._heicFile = null;
    // Sync both zones' preview/placeholder state.
    const fromZone = fromInput.closest('.upload-zone');
    const toZone = toInput.closest('.upload-zone');
    if (fromZone && toZone) {
        const fromPrev = fromZone.querySelector('.upload-preview');
        const toPrev = toZone.querySelector('.upload-preview');
        const fromImg = fromPrev && fromPrev.querySelector('img');
        const toImg = toPrev && toPrev.querySelector('img');
        if (fromImg && toImg && fromImg.getAttribute('src')) {
            toImg.src = fromImg.src;
            fromImg.removeAttribute('src');
        }
        if (toPrev) toPrev.style.display = 'block';
        const toPh = toZone.querySelector('.upload-placeholder');
        if (toPh) toPh.style.display = 'none';
        toZone.classList.add('has-file');
        if (fromPrev) fromPrev.style.display = 'none';
        const fromPh = fromZone.querySelector('.upload-placeholder');
        if (fromPh) fromPh.style.display = fromZone.dataset.phDisplay || 'block';
        fromZone.classList.remove('has-file');
    }
    return file;
}

// NOTE (2026-09-28): the submit button must stay ENABLED with no photo attached.
// A disabled-until-upload gate shipped for ~10h and cut signup→first-submit from
// 97% to 83%: new mobile users tap the greyed button, get zero feedback, read the
// "Only 1 credit left" bar beneath it and leave for /dashboard/credits. The
// enabled-tap path below (toast + scroll-to-zone + red highlight) is a real
// onboarding step — 11 of 13 users who hit it on 09-27 went on to submit.

document.addEventListener('DOMContentLoaded', () => {
    const form = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
    if (form) {
        form.addEventListener('submit', async function (e) {
            e.preventDefault();
            if (window.gradingInProgress) return;

            const submitBtn = form.querySelector('button[type="submit"]');
            const allSubmitBtns = form.querySelectorAll('button[type="submit"]');
            const resultsSection = document.querySelector('#results-section');
            const resultsContainer = document.querySelector('#results-container');
            const placeholder = document.querySelector('#results-placeholder');
            const endpoint = form.dataset.endpoint || form.action;
            const frontInput = form.querySelector('input[name="image_front"]');
            const backInput = form.querySelector('input[name="image_back"]');
            const frontZone = document.getElementById('upload-zone-front');
            const funnel = (type, meta) => { pgcGradeEvent(type, meta); };
            // New attempt per submit — correlates this submit's rejects,
            // beam, response, recovery, and reject-modal actions under att.
            pgcNewAttempt();

            funnel('grade_form_submit', {
                has_front: pgcStagedFile(frontInput) ? 1 : 0,
                has_back: pgcStagedFile(backInput) ? 1 : 0,
                front_kb: pgcStagedFile(frontInput) ? Math.round(pgcStagedFile(frontInput).size / 1024) : 0
            });

            // Upsell modal already open — the overlay blocks real clicks, but a
            // programmatic/keyboard submit must not start an upload behind it.
            if (window.CreditUpsell && typeof window.CreditUpsell.isOpen === 'function' && window.CreditUpsell.isOpen()) return;

            // Wrong-slot rescue: the user's ONLY photo sits in the back zone
            // (camera tap landed on the back zone's button — 19 users in 14d
            // dead-ended here, e.g. u4617 who was told "no photo" while staring
            // at their staged capture). Promote it to the front slot and carry
            // on with the submit — a card photo is always gradeable as a front.
            if (!pgcStagedFile(frontInput) && pgcStagedFile(backInput)) {
                // …but NOT right after a completed grade. 6 of the first 7
                // promotions (2026-10-24/25) were users who had just graded a
                // front, saw the form reset, and dropped the BACK of that same
                // card into the back zone to add it — we charged a credit,
                // graded a card back as a new card, and withheld the ID
                // ("card back uploaded as primary", u4899 ×2). A real photo in
                // the back slot after a fresh result is a back. Tell them how
                // backs actually work instead of guessing.
                const recentGrade = (window.pgcGradeResultAt && (Date.now() - window.pgcGradeResultAt) < 30 * 60 * 1000)
                    || !!document.querySelector('#results-container .ximilar-result');
                if (recentGrade) {
                    funnel('grade_back_only_blocked', { since_result_s: window.pgcGradeResultAt ? Math.round((Date.now() - window.pgcGradeResultAt) / 1000) : null });
                    showAlert('That\'s the back photo — backs are graded together with their front, not on their own. Add the front photo again (the same one is fine), keep the back, and tap Grade. Front + back is 1.5 credits.', 'warning');
                    if (frontZone) {
                        pgcEnsureZoneHintCss();
                        frontZone.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        frontZone.classList.add('pgc-needs-photo');
                        setTimeout(() => frontZone.classList.remove('pgc-needs-photo'), 3600);
                    }
                    return;
                }
                const promoted = pgcPromoteStagedFile(backInput, frontInput);
                if (promoted) {
                    funnel('grade_back_promoted', { size_kb: Math.round(promoted.size / 1024) });
                    showAlert('Using your photo as the card front — grading it now. You can add a back photo on your next grade.', 'info');
                    if (typeof syncSubmitButtons === 'function') syncSubmitButtons(form);
                }
            }

            if (!pgcStagedFile(frontInput)) {
                funnel('grade_client_reject', { reason: 'no_front' });
                showAlert('No photo yet — upload your card\'s front photo first, then tap Grade.', 'error');
                if (frontZone) {
                    // Highlight alone loses when the zone is off-screen — a
                    // mobile user who taps Grade with no file sees a toast and
                    // nothing else. Bring the zone to them and hold a pulsing
                    // ring + an explicit "add your front photo" hint on it so
                    // the fix is obvious, not just a red flash.
                    pgcEnsureZoneHintCss();
                    frontZone.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    frontZone.classList.add('pgc-needs-photo');
                    let hint = frontZone.querySelector('.pgc-zone-hint');
                    if (!hint) {
                        hint = document.createElement('div');
                        hint.className = 'pgc-zone-hint';
                        hint.textContent = '📸 Tap here to add your card\'s front photo';
                        (frontZone.querySelector('.upload-preview') || frontZone).appendChild(hint);
                    }
                    setTimeout(() => {
                        frontZone.classList.remove('pgc-needs-photo');
                        const h = frontZone.querySelector('.pgc-zone-hint');
                        if (h && h.parentNode) h.parentNode.removeChild(h);
                    }, 3600);
                }
                // Don't leave the tap as a dead end — the submit event still
                // carries user activation, so opening the native picker here
                // turns "Grade with no photo" into "pick a photo" (iOS shows
                // the camera/library sheet). Ignore failures: the alert +
                // highlight above are the fallback UX.
                try { if (frontInput) frontInput.click(); } catch (e) {}
                return;
            }

            // Prefer the browser-converted JPEG when the original was HEIC
            // (main.js converts on selection and stashes it on the input).
            const frontFile = frontInput._heicFile || frontInput.files[0];
            const frontIsHeic = !frontInput._heicFile && (
                /\.(heic|heif)$/i.test(frontFile.name || '') ||
                ['image/heic','image/heif','image/heic-sequence','image/heif-sequence'].includes((frontFile.type || '').toLowerCase())
            );
            if (frontIsHeic) {
                funnel('grade_client_reject', { reason: 'heic_pending' });
                // Conversion is already running — instead of bouncing the user,
                // submit for them the moment it lands (bounded, once). If it
                // fails, main.js's own error alert covers it.
                if (!form._heicAutoSubmit) {
                    form._heicAutoSubmit = true;
                    showAlert('Still converting your iPhone photo — grading will start automatically…', 'info');
                    let waited = 0;
                    const iv = setInterval(() => {
                        waited += 500;
                        if (frontInput._heicFile && !window.gradingInProgress) {
                            clearInterval(iv);
                            form._heicAutoSubmit = false;
                            form.requestSubmit();
                        } else if (waited >= 30000) {
                            clearInterval(iv);
                            form._heicAutoSubmit = false;
                        }
                    }, 500);
                } else {
                    showAlert('Still converting your iPhone photo — try again in a moment.', 'error');
                }
                return;
            }

            // Stub guard: no real photo is under 256B. iOS IndexedDB can hand
            // back a byteless File shell (~35B) whose multipart payload can't
            // be read server-side — reject client-side instead of feeding it.
            if (frontFile.size < 256) {
                funnel('grade_client_reject', { reason: 'stub_file', size_b: frontFile.size });
                showAlert('Your photo didn\'t attach completely — please pick it again.', 'error');
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif'];
            // _heicRawFallback = client HEIC decode failed; the raw file goes
            // to the server for Imagick transcode — don't bad_type it away.
            if (!frontFile._heicRawFallback && !allowedTypes.includes(frontFile.type)) {
                funnel('grade_client_reject', { reason: 'bad_type', mime: frontFile.type || '' });
                showAlert('Please upload a JPG, PNG, GIF, WebP, or AVIF image.', 'error');
                return;
            }
            const maxSize = 16 * 1024 * 1024;
            // _heicRawFallback can't be downscaled client-side — the server
            // pre-scales it via Imagick, so don't dead-end it here.
            if (frontFile.size > maxSize && !frontFile._heicRawFallback) {
                const mb = (frontFile.size / (1024 * 1024)).toFixed(1);
                funnel('grade_client_reject', { reason: 'too_large', size_mb: mb });
                showAlert(`Image is ${mb}MB — maximum is 16MB. Please resize or compress.`, 'error');
                return;
            }
            // Warn before the long grade starts if an attached back photo will be
            // dropped for insufficient balance — otherwise they find out ~90s later.
            const backAttached = (backInput && backInput.files && backInput.files.length) || (backInput && backInput._heicFile);
            if (backAttached && window.IS_LOGGED_IN) {
                const bc = parseFloat(window.GRADE_BACK_CREDITS) || 0;
                const gc = parseFloat(window.GRADE_CREDITS) || 1;
                if (bc > 0 && (parseFloat(window.CREDIT_BALANCE) || 0) < gc + bc) {
                    showAlert('Heads up: your balance can\'t cover the +' + bc + ' back add-on — this grade will cover the front only.', 'warning');
                }
            }

            // NOTE (2026-10-11): client-side affordability pre-check removed —
            // an awaited preflight modal could swallow submits entirely (user
            // sees upsell, grade never fires). The server still returns 402 →
            // CreditUpsell.show() on a real insufficient balance, which is the
            // same upsell shown without blocking the submit path.
            //
            // 2026-10-21: re-added as a NON-BLOCKING zero-balance guard. A
            // logged-in user with 0 credits (welcome credit spent) used to pay
            // an 11s upload round-trip for a 402, then loop: submit → upsell →
            // dismiss → re-submit (user 4591: 5 attempts in 22 min, churn).
            // At 0 (< front cost) the upsell opens instantly and the request
            // is never sent — no phantom polls, no repeated dead trips. The
            // back-add-on case (balance ≥ front but < front+back) is NOT
            // blocked here: the server grades front-only by design.
            const frontCost = parseFloat(window.GRADE_CREDITS) || 1;
            if (window.IS_LOGGED_IN && (parseFloat(window.CREDIT_BALANCE) || 0) < frontCost) {
                // The baked balance can be STALE (Safari bfcache restores the
                // page after a top-up; a footer DB hiccup bakes 0) — blocking
                // on it would stop a paying user cold. Re-verify with the
                // server first (bounded 4s, no-store). Only a CONFIRMED
                // shortfall blocks; any fetch uncertainty falls through to the
                // authoritative server-side 402 (which shows the same upsell).
                let confirmedShort = false;
                try {
                    const bAc = window.AbortController ? new AbortController() : null;
                    const bT = bAc ? setTimeout(function () { try { bAc.abort(); } catch (e) {} }, 4000) : 0;
                    const bR = await fetch('/api/credit-status.php', { credentials: 'same-origin', cache: 'no-store', signal: bAc ? bAc.signal : undefined });
                    if (bT) clearTimeout(bT);
                    const bJ = await bR.json();
                    if (bJ && bJ.success && typeof bJ.balance === 'number') {
                        window.CREDIT_BALANCE = bJ.balance;
                        confirmedShort = bJ.balance < frontCost;
                    }
                } catch (e) { /* uncertain — let the server decide via 402 */ }
                if (confirmedShort) {
                    hideGradingModal();
                    window.gradingInProgress = false;
                    funnel('grade_gate_reject', { required: frontCost, available: parseFloat(window.CREDIT_BALANCE) || 0, via: 'client_precheck' });
                    if (window.CreditUpsell) {
                        pgcStashPendingScan(frontInput, backInput, true);
                        window.CreditUpsell.show(frontCost, parseFloat(window.CREDIT_BALANCE) || 0);
                    } else {
                        window.location.href = '/dashboard/credits';
                    }
                    return;
                }
            }

            allSubmitBtns.forEach(b => { b.disabled = true; });
            window.gradingInProgress = true;

            try {
                // Raw-HEIC fallback can't Image-decode by definition — the
                // server transcodes it, so a 'corrupt' verdict here would be
                // a false reject. Same for the decode-pressure class
                // (_decodeFallback: valid JPEGs the browser failed to decode
                // at attach time — the server's Imagick handles them) and for
                // files that pass createImageBitmap but fail <img> decode
                // (preview_render class) — use the tolerant decoder so
                // attach-time and submit-time agree.
                if (!frontFile._heicRawFallback && !frontFile._decodeFallback) {
                    if (typeof decodeToDrawable === 'function') {
                        const d = await decodeToDrawable(frontFile);
                        if (d && d.bitmap && d.bitmap.close) d.bitmap.close();
                    } else {
                        const testUrl = URL.createObjectURL(frontFile);
                        const testImg = new Image();
                        await new Promise((resolve, reject) => {
                            testImg.onload = resolve;
                            testImg.onerror = () => reject(new Error('corrupted'));
                            testImg.src = testUrl;
                        });
                        URL.revokeObjectURL(testUrl);
                    }
                }
            } catch (e) {
                window.gradingInProgress = false;
                allSubmitBtns.forEach(b => { b.disabled = false; });
                funnel('grade_client_reject', { reason: 'corrupt' });
                showAlert('This image file appears to be corrupted. Please try a different image.', 'error');
                return;
            }

            let previewUrl = null;
            if (frontFile && !frontFile._heicRawFallback && !frontFile._decodeFallback && typeof URL.createObjectURL === 'function') {
                previewUrl = URL.createObjectURL(frontFile);
            }

            let dims = { width: 0, height: 0 };
            try {
                if (typeof getImageDimensions === 'function') {
                    dims = await getImageDimensions(frontFile);
                } else if (frontFile && frontFile.type.startsWith('image/')) {
                    const imgEl = await new Promise((resolve, reject) => {
                        const url = URL.createObjectURL(frontFile);
                        const i = new Image();
                        i.onload = () => { URL.revokeObjectURL(url); resolve(i); };
                        i.onerror = () => { URL.revokeObjectURL(url); reject(new Error('load failed')); };
                        i.src = url;
                    });
                    dims = { width: imgEl.naturalWidth, height: imgEl.naturalHeight };
                }
            } catch (e) {
                // Fallback: use default countdown if dimensions can't be read
            }
            const backInputEl = form.querySelector('input[name="image_back"]');
            const hasBackPhoto = !!pgcStagedFile(backInputEl);
            const countdownSec = estimateCountdown(dims.width, dims.height, hasBackPhoto);
            showGradingModal(previewUrl, countdownSec);
            let response = null;
            let csrfRetried = false;
            let transportRetried = false;
            const submitStartMs = Date.now();
            const sinceSec = Math.floor(submitStartMs / 1000);
            // Phase gate (u4751, 2026-10-22): photos are staged in chunks
            // BEFORE the grade POST. Until that POST is sent no job can exist,
            // so grade-status can only answer "nothing here" — the stall
            // watchdog, recovery poll and progress poll used to run during a
            // slow upload, declare "Grading finished but the result could not
            // be loaded", release the form mid-upload and invite a resubmit
            // that doubled the load. They now start once the POST goes out.
            let gradePosted = false, postStartMs = 0;
            let supersededQuietly = false;
            // Single-flight: a new submit cancels any older staging loop
            // still holding the uplink.
            if (window.__pgcStaging && typeof window.__pgcStaging.cancel === 'function') window.__pgcStaging.cancel();
            const stagingCtl = {
                cancelled: false, switching: false, xhr: null,
                cancel() { this.cancelled = true; try { if (this.xhr) this.xhr.abort(); } catch (e) {} }
            };
            window.__pgcStaging = stagingCtl;
            // Result recovery — a submit fetch that stalls or dies at the
            // network layer usually still completes server-side (the result
            // lands in action_history and credits settle there). Poll
            // grade-status so the user gets the result they paid for instead
            // of a dead spinner or a blind retry that double-bills.
            let recoveryTimer = null;
            let delivered = false;
            // What delivered the outcome: 'fetch' (the submit response — the
            // source of truth), or a poll verdict ('poll_done'/'poll_failed'/
            // 'poll_empty'/'poll_timeout'). An empty poll is inconclusive — if
            // the fetch later resolves with the real result or reject, it wins.
            let deliveredBy = null;
            const stopRecovery = () => {
                // Only the closure that owns the live timer may clear the
                // pending flag — a second submit's stopRecovery is a no-op.
                // Releasing recovery also frees the submit buttons — they stay
                // disabled while a poll is live so a resubmit can't race the
                // in-flight grade into a 409.
                if (recoveryTimer) { clearInterval(recoveryTimer); recoveryTimer = null; window.pgcGradePending = false; allSubmitBtns.forEach(b => { b.disabled = false; }); }
            };
            // Dead-tab recovery hook: a suspended/discarded tab kills the
            // submit XHR and every timer but keeps this DOM — the modal wakes
            // looking alive with nothing left to finish it. The global
            // visibility/pageshow listener calls this so a restored tab polls
            // immediately; a live recoveryTimer or a delivered verdict makes
            // the inner startRecoveryPolling call a no-op.
            const wakeModal = document.getElementById('grading-loading-modal');
            if (wakeModal) {
                wakeModal._pgcWakePoll = function () {
                    if (!delivered) startRecoveryPolling('wake');
                };
            }
            const startRecoveryPolling = (trigger) => {
                // No grade POST yet = nothing server-side to recover (see the
                // phase gate above); polling now would only return a false verdict.
                if (recoveryTimer || delivered || !gradePosted) return;
                window.pgcGradePending = true;
                const pollUrl = '/api/grade-status.php?since=' + sinceSec + '&via=' + encodeURIComponent(trigger || 'poll');
                let pollStart = Date.now();
                let polls = 0;
                let pollInFlight = false;
                let lastPollHttpStatus = 0;
                let lastPollActionId = null;
                // First time the server reports this submission is waiting on
                // the global job cap. Used to keep polling while it waits (the
                // queue is progress, not a hang) with a hard ceiling.
                let queueWaitStart = 0;
                const QUEUE_WAIT_MAX_MS = 900000;
                // Same for a RUNNING job: the comps tail can hold a job minutes
                // past the countdown (observed 204-263s totals with the grade
                // itself done at ~30-60s). Pre-fix, 'running' fell through to
                // the 3-minute timeout and told the user to "check history
                // later" while their result was seconds away — 4 users hit it
                // in one 28-minute window on 10-21.
                let runWaitStart = 0;
                const RUN_WAIT_MAX_MS = 720000; // 12 min hard ceiling
                // Soft wait (replaces the old 3-min "give up" toast): the modal
                // closes with an honest panel, polling continues in the
                // background (throttled), and a late result raises a "ready"
                // notice instead of silently landing only in history.
                let softWaitAt = 0;
                // Freshness floor for shared status responses: the original poll's
                // first own fetch ran 5s after start (job registered by then) —
                // only reuse responses whose fetch began >=4s after this start.
                const recoveryStartAt = Date.now() + 4000;
                const SOFT_WAIT_MAX_MS = 900000; // stop background polling after 15 min
                recoveryTimer = setInterval(async () => {
                    if (delivered) { stopRecovery(); return; }
                    // setInterval doesn't await the async callback — on a slow
                    // connection fetches stack up and each straggler re-hits
                    // the endpoint after 'done' already landed.
                    if (pollInFlight) return;
                    pollInFlight = true;
                    polls++;
                    try {
                        // Shared, de-duplicated with the progress tick (15s abort inside).
                        const r = await pgcGradeStatusGet(sinceSec, trigger || 'poll', recoveryStartAt);
                        lastPollHttpStatus = r.status;
                        const st = r.body;
                        lastPollActionId = (st && st.action_id) || (st && st.grade && st.grade.action_id) || lastPollActionId;
                        if (!st || !st.success) return; // transient — next tick retries
                        if (softWaitAt) {
                            // Background soft wait: a NEW grade owns the UI now → stop.
                            if (window.gradingInProgress || Date.now() - softWaitAt > SOFT_WAIT_MAX_MS) { stopRecovery(); return; }
                            if (st.status === 'done' && st.grade) {
                                stopRecovery();
                                funnel('grade_soft_wait_delivered', { elapsed_ms: Date.now() - submitStartMs, polls: polls });
                                showGradeReadyNotice(lastPollActionId);
                            } else if (st.status === 'failed') {
                                stopRecovery();
                                showAlert(st.error || pgcT('glm.late_failed', 'That grade couldn\'t be completed — any credits used were refunded.'), 'warning');
                            } else if (st.status === 'deferred') {
                                stopRecovery(); // server owns it now: history + email + bell
                            }
                            return;
                        }
                        if (st.status === 'done' && st.grade) {
                            stopRecovery();
                            if (delivered) return;
                            delivered = true;
                            deliveredBy = 'poll_done';
                            // The poll's HTTP status and the detached job outcome are distinct signals.
                            const responseMeta = {
                                http_status: r.status,
                                http_status_source: 'transport',
                                grade_outcome: st.grade_outcome || 'completed',
                                action_id: lastPollActionId,
                                success: 1,
                                error: '',
                                via: 'poll',
                                polls: polls,
                                elapsed_ms: Date.now() - submitStartMs
                            };
                            if (trigger !== 'poll') {
                                // The failed submit event remains a transport failure; this records its recovered job outcome.
                                responseMeta.recovered = 1;
                                funnel('grade_recovered', { trigger: trigger, polls: polls, elapsed_ms: Date.now() - submitStartMs });
                            }
                            funnel('grade_response', responseMeta);
                            handleGradeSuccess(st.grade);
                        } else if (st.status === 'failed' || (st.status === 'done' && !st.grade)) {
                            stopRecovery();
                            if (delivered) return;
                            delivered = true;
                            deliveredBy = st.status === 'failed' ? 'poll_failed' : 'poll_empty';
                            // Terminal outcome reached — log grade_response so
                            // the funnel's "API response received" stage counts
                            // poll-resolved failures like synchronous ones;
                            // only true mid-flight abandons stay uncounted.
                            funnel('grade_response', {
                                http_status: r.status,
                                http_status_source: 'transport',
                                grade_outcome: st.grade_outcome || (st.reject && st.reject.noncard ? 'noncard_reject' : (st.reject && st.reject.retake ? 'photo_reject' : (st.status === 'failed' ? 'detached_failed' : 'empty_result'))),
                                action_id: lastPollActionId,
                                success: 0,
                                error: String(st.error || (st.status === 'failed' ? 'failed' : 'empty_result')).slice(0, 120),
                                retake: (st.reject && st.reject.retake) ? 1 : 0,
                                via: 'poll',
                                polls: polls,
                                elapsed_ms: Date.now() - submitStartMs
                            });
                            funnel('grade_recovery_' + (st.status === 'failed' ? 'failed' : 'empty'), { trigger: trigger, polls: polls });
                            hideGradingModal();
                            window.gradingInProgress = false;
                            if (st.reject && (st.reject.retake || st.reject.noncard)) {
                                // Async-mode photo/noncard reject — the exit
                                // path's payload was persisted on the failed
                                // row and grade-status replayed it; show the
                                // same rejection modal the 422 renders.
                                showUploadRejectedModal(st.reject, previewUrl);
                            } else {
                                showAlert(st.status === 'failed'
                                    ? (st.error || (PGC_XIM_SLOW
                                        ? 'Our grading provider couldn\'t complete this grade — not your photo. Any credits used were refunded and retrying is free.'
                                        : 'That grade didn\'t finish — any credits used were refunded. Please try again.'))
                                    // A row id means a result exists in history; no row
                                    // means the grade never started server-side.
                                    : (lastPollActionId
                                        ? 'Grading finished but the result could not be loaded — check your grading history in a moment.'
                                        : 'That grade didn\'t start on our side, so no credits were used. Please try again.'), 'warning');
                            }
                        } else if (st.status === 'deferred') {
                            // Provider result is late — the grade is safe in
                            // grade_deferred and will be delivered by history +
                            // email + bell. End the modal instead of an endless
                            // spinner; the credit stays held (refund if it fails).
                            stopRecovery();
                            if (delivered) return;
                            delivered = true;
                            deliveredBy = 'poll_deferred';
                            funnel('grade_response', {
                                http_status: r.status, http_status_source: 'transport', grade_outcome: 'deferred',
                                action_id: null, success: 0, error: '', via: 'poll', polls: polls,
                                elapsed_ms: Date.now() - submitStartMs
                            });
                            hideGradingModal();
                            window.gradingInProgress = false;
                            showDeferredPanel(st);
                        } else if (st.status === 'queued') {
                            // Waiting on the global concurrent-job cap. Say so
                            // with the queue position instead of leaving the
                            // spinner unexplained (2026-10-18 — the cap is
                            // default 5 jobs and the comps tail can hold a slot
                            // for an hour, so this wait can be minutes long).
                            if (!queueWaitStart) queueWaitStart = Date.now();
                            const glmQ = document.getElementById('grading-loading-modal');
                            if (glmQ) {
                                const lblQ = glmQ.querySelector('#glm-countdown-label');
                                if (lblQ) { lblQ.textContent = 'Waiting for a grading slot'; lblQ.style.color = '#fbbf24'; lblQ.style.fontWeight = '700'; }
                                const numQ = glmQ.querySelector('#glm-countdown-num');
                                if (numQ) numQ.textContent = '…';
                                const stQ = glmQ.querySelector('#glm-status');
                                if (stQ) {
                                    const pos = parseInt(st.queue_position || 0, 10);
                                    const depth = parseInt(st.queue_depth || 0, 10);
                                    const cap = parseInt(st.capacity || 0, 10);
                                    let msg = (pos > 1 && depth > 1)
                                        ? 'Your card is ' + pos + ' of ' + depth + ' waiting for a grading slot.'
                                        : 'Your card is next in line for a grading slot.';
                                    msg += ' It starts automatically — no need to resubmit'
                                        + (cap > 0 ? ' (' + cap + ' grades run at once).' : '.');
                                    stQ.style.opacity = '1';
                                    stQ.textContent = msg;
                                }
                            }
                            // The queue is progress: push the abandon deadline out
                            // so we deliver the result the moment the job starts,
                            // with a hard ceiling so we never poll forever.
                            if (Date.now() - queueWaitStart < QUEUE_WAIT_MAX_MS) pollStart = Date.now();
                        } else if (st.status === 'running' && Date.now() - pollStart <= 180000) {
                            // Actively working — progress, not a hang. Keep the
                            // deadline moving up to the 12-min ceiling; once the
                            // ceiling passes, pollStart freezes and 180s later
                            // this guard fails, dropping into the timeout branch
                            // below. Meanwhile, once the original countdown is
                            // clearly blown, switch the modal to honest
                            // "finalizing" copy instead of a frozen timer.
                            if (!runWaitStart) runWaitStart = Date.now();
                            if (Date.now() - runWaitStart < RUN_WAIT_MAX_MS) pollStart = Date.now();
                            // Measured from the grade POST — a slow photo upload
                            // before it is not "grading time".
                            if (Date.now() - (postStartMs || submitStartMs) > 150000) {
                                const glmR = document.getElementById('grading-loading-modal');
                                if (glmR) {
                                    const lblR = glmR.querySelector('#glm-countdown-label');
                                    if (lblR) { lblR.textContent = 'Finalizing your grade'; lblR.style.color = '#34d399'; lblR.style.fontWeight = '700'; }
                                    const numR = glmR.querySelector('#glm-countdown-num');
                                    if (numR) numR.textContent = '…';
                                    const stR = glmR.querySelector('#glm-status');
                                    if (stR) {
                                        stR.style.opacity = '1';
                                        // Only claim "grade is done" when the server reported a
                                        // card stage; while the provider is busy that was false.
                                        stR.textContent = glmR._providerBusy
                                            ? pgcT('glm.provider_slow', 'Our AI grading provider is still working through a backlog — your result will appear automatically, or we\'ll email you.')
                                            : pgcT('glm.comps_tail', 'Your grade is done — gathering market prices and comps. This can take a few extra minutes; the result will appear automatically.');
                                    }
                                }
                            }
                        } else if (Date.now() - pollStart > 180000) {
                            // ~3 min with no progress signal (the server defers
                            // slow-provider grades by ~75s, so this is now an
                            // edge case: lost lock/row visibility, comps tail
                            // past the run ceiling). Don't strand the user on a
                            // spinner and don't give up either — close the modal
                            // with an honest panel and keep a background poll
                            // (throttled; see softWaitAt) so a late result still
                            // surfaces as a "ready" notice.
                            if (delivered) return;
                            delivered = true;
                            softWaitAt = Date.now();
                            deliveredBy = 'poll_timeout';
                            // The background poll must not block a new grade:
                            // re-enable submit (a live poll normally disables it).
                            window.pgcGradePending = false;
                            allSubmitBtns.forEach(b => { b.disabled = false; });
                            funnel('grade_response', {
                                http_status: lastPollHttpStatus,
                                http_status_source: 'transport',
                                grade_outcome: 'poll_timeout',
                                action_id: lastPollActionId,
                                success: 0,
                                error: 'poll_timeout',
                                via: 'poll',
                                polls: polls,
                                elapsed_ms: Date.now() - submitStartMs
                            });
                            funnel('grade_still_waiting', { trigger: trigger, polls: polls });
                            hideGradingModal();
                            window.gradingInProgress = false;
                            showDeferredPanel({ variant: 'slow' });
                        }
                    } catch (e) { /* poll transport hiccup — next tick retries */ }
                    finally { pollInFlight = false; }
                }, 5000);
            };
            const handleGradeSuccess = (result) => {
                // Mark this grade as seen — the return-visit pending check
                // below only banners grades newer than the last rendered one.
                try { if (result.action_id) localStorage.setItem('pgcSeenGradeId', String(result.action_id)); } catch (e) {}
                if (placeholder) placeholder.style.display = 'none';
                if (resultsSection) resultsSection.style.display = 'block';
                window.pgcGradeResultAt = Date.now();
                const dataToUse = result.data || result;
                // displayResults returns a report URL when the record was
                // persisted (the ?result= server-render path) — navigate after
                // the reveal, anchored at #results-section, instead of letting
                // the reload dump the user at page top while the report sits
                // thousands of px down.
                const reportUrl = displayResults(dataToUse, endpoint, resultsContainer, result);
                // Persisted grade: pull the ?result= server render and inject
                // it into this page — no hard reload. The URL still becomes
                // ?result=N so refresh/share/bookmark reproduce the report,
                // and any failure falls back to the old full navigation.
                const showReportInline = async (url) => {
                    try {
                        const res = await fetch(url.split('#')[0], { credentials: 'same-origin' });
                        if (!res.ok) throw new Error('http ' + res.status);
                        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
                        const fresh = doc.querySelector('#results-container');
                        if (!fresh || !fresh.innerHTML.trim()) throw new Error('empty server render');
                        resultsContainer.innerHTML = fresh.innerHTML;
                        try { history.pushState(null, '', url); } catch (e) {}
                        if (window.XimilarResults && typeof window.XimilarResults.ensureStyles === 'function') window.XimilarResults.ensureStyles();
                        if (window.PGCRecord && typeof window.PGCRecord.refresh === 'function') window.PGCRecord.refresh();
                        const reportTop = document.getElementById('xim-report-top') || resultsSection;
                        if (reportTop && reportTop.scrollIntoView) {
                            reportTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        setTimeout(() => { window.gradingInProgress = false; }, 5000);
                    } catch (e) {
                        window.location.href = url;
                    }
                };
                const showReport = () => {
                    hideGradingModal();
                    if (reportUrl) {
                        showReportInline(reportUrl);
                        return;
                    }
                    // Never assume the report mount exists: a page whose results
                    // block is absent (server-side gate, cached HTML) used to throw
                    // here and leave the reveal stuck with no report rendered.
                    if (resultsSection && resultsSection.scrollIntoView) {
                        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                    setTimeout(() => { window.gradingInProgress = false; }, 5000);
                };

                if (result.back_skipped_low_credits) {
                    // Front graded, but the optional back photo was dropped
                    // because the balance couldn't cover the add-on. Tell the
                    // user instead of silently ignoring their upload.
                    showAlert('Front photo graded. Your back photo was skipped — it needs +' + (result.back_cost || 0.5) + ' credits and your balance is too low.', 'warning');
                }
                if (result.back_failed) {
                    // Back add-on was charged but the grader couldn't analyze
                    // the back photo (auto-refunded server-side). Persistent
                    // notice panel is rendered inside the results too.
                    showAlert(result.back_error === 'different_card'
                        ? 'The second photo appears to show a different card. Only the front was graded; the back add-on was refunded.'
                        : 'Front photo graded, but the back photo couldn\'t be analyzed — the add-on was refunded. Retake the back straight-on on a plain contrasting background.', 'warning');
                }

                const record = (dataToUse.records && dataToUse.records[0]) || {};
                const recGrades = record.grades || {};
                const rawGrade = recGrades.final != null ? recGrades.final
                    : (record.final_grade != null ? record.final_grade
                    : (record.grade != null ? record.grade
                    : (recGrades.overall != null ? recGrades.overall
                    : (dataToUse.final_grade != null ? dataToUse.final_grade : null))));
                const estGrade = (rawGrade !== '' && rawGrade != null) ? rawGrade : null;
                const condition = recGrades.condition != null ? recGrades.condition
                    : (record.final_grade_label != null ? record.final_grade_label
                    : (record.condition != null ? record.condition : null));
                const skipRevealCheckbox = document.getElementById('skip-reveal');
                const skipReveal = Boolean(skipRevealCheckbox && skipRevealCheckbox.checked);
                resetUploadForm();

                if (estGrade != null && !skipReveal) {
                    const completedCountdown = document.getElementById('glm-countdown-wrap');
                    if (completedCountdown) completedCountdown.style.display = 'none';
                    showGradeReveal(estGrade, condition, previewUrl, showReport);
                } else {
                    const modal = document.getElementById('grading-loading-modal');
                    if (modal) stopCountdown(modal);
                    setTimeout(() => {
                        showReport();
                        // Notify promo engine that grading is complete (no reveal was shown)
                        window.dispatchEvent(new CustomEvent('pgc:gradeRevealComplete'));
                    }, 300);
                }
            };
            // Stall watchdog — at 75s we switch the modal to honest "still
            // processing" copy (frozen countdown + rotating status both get
            // stopped so the message isn't clobbered) and start polling for
            // the completed result. The funnel event fires at 120s instead:
            // ~13% of grades legitimately run 75-120s (slow Ximilar queue +
            // comps tail), so logging at 75s made a p87 latency percentile
            // look like an error — 120s is ~p99, i.e. genuinely abnormal.
            // Armed by armWatchdogs() once the grade POST is sent (phase gate).
            let stallTimer = null, stallLogTimer = null;
            const armWatchdogs = () => {
            stallTimer = setTimeout(function () {
                const glmModal = document.getElementById('grading-loading-modal');
                if (glmModal) {
                    try {
                        const iv = JSON.parse(glmModal.dataset.intervals || '{}');
                        if (iv.countdown) clearInterval(iv.countdown);
                        if (iv.status) clearInterval(iv.status);
                    } catch (e) {}
                    const lbl = glmModal.querySelector('#glm-countdown-label');
                    if (lbl) { lbl.textContent = 'Still working — result appears automatically'; lbl.style.color = '#fbbf24'; lbl.style.fontWeight = '700'; }
                    const num = glmModal.querySelector('#glm-countdown-num');
                    if (num) num.textContent = '…';
                    const stEl = glmModal.querySelector('#glm-status');
                    if (stEl) { stEl.style.opacity = '1'; stEl.textContent = PGC_XIM_SLOW
                        ? 'Our grading provider is running behind right now — your photos are fine. The result will appear here automatically when it lands.'
                        : 'This one is taking longer than usual — your grade is still processing on our servers and will appear here when it\'s done.'; }
                }
                startRecoveryPolling('stall');
            }, 75000);
            stallLogTimer = setTimeout(function () {
                const _bi = form.querySelector('input[name="image_back"]');
                funnel('grade_submit_stall', { elapsed_s: 120, has_back: (_bi && _bi.files && _bi.files.length) ? 1 : 0 });
            }, 120000);
            };
            try {
                pgcWakeLock.acquire();
                // iOS keep-open hint — the modal's own status line rotates;
                // this is a static sibling line injected once.
                if (PGC_IOS_UA && !document.getElementById('pgc-ios-keep-open')) {
                    const glm = document.getElementById('grading-loading-modal');
                    if (glm) {
                        const stEl = glm.querySelector('#glm-status');
                        const hint = document.createElement('div');
                        hint.id = 'pgc-ios-keep-open';
                        hint.style.cssText = 'margin-top:8px;font-size:0.78rem;color:#fbbf24;';
                        hint.textContent = 'Tip: keep this page open and your screen awake while photos upload.';
                        if (stEl && stEl.parentNode) stEl.parentNode.insertBefore(hint, stEl.nextSibling);
                        else glm.appendChild(hint);
                    }
                }
                // Upload progress — fetch() can't report upload progress on
                // the browsers that matter, so the submit POST goes through
                // XHR. Multi-MB bodies on cellular take 10-30s of the total
                // wait; a visible bar turns "is it frozen?" into progress,
                // then swaps to "analyzing" once the body lands.
                const uploadProg = (frac, detail) => {
                    const glm = document.getElementById('grading-loading-modal');
                    if (!glm) return;
                    let up = glm.querySelector('#pgc-upload-prog');
                    if (!up) {
                        up = document.createElement('div');
                        up.id = 'pgc-upload-prog';
                        up.style.cssText = 'margin:0 0 16px;';
                        up.innerHTML =
                            '<div id="pgc-upload-label" style="font-size:12px;color:#fbbf24;font-weight:600;margin:0 0 6px;">Uploading photos… 0%</div>' +
                            '<div style="height:6px;border-radius:999px;background:rgba(255,255,255,0.1);overflow:hidden;">' +
                            '<div id="pgc-upload-bar" style="height:100%;width:0%;border-radius:999px;background:linear-gradient(90deg,#22c55e,#3b82f6);transition:width 0.3s ease;"></div></div>';
                        const cw = glm.querySelector('#glm-countdown-wrap');
                        if (cw && cw.parentNode) cw.parentNode.insertBefore(up, cw);
                        else glm.querySelector('#glm-content').appendChild(up);
                    }
                    const pct = Math.round(frac * 100);
                    const bar = up.querySelector('#pgc-upload-bar');
                    const lbl = up.querySelector('#pgc-upload-label');
                    if (bar) bar.style.width = pct + '%';
                    if (lbl) lbl.textContent = pct >= 100 ? 'Upload complete — analyzing…' : 'Uploading photos… ' + pct + '%' + (detail ? ' · ' + detail : '');
                    if (typeof glm._fitCard === 'function') glm._fitCard();
                };
                const selectedFile = (input) => input ? (input._heicFile || (input.files && input.files[0] || null)) : null;
                const chunkMin = 768 * 1024;
                // Chunked staging, built for very slow / flaky uplinks (u4751:
                // ~5 KB/s — a 256 KB chunk took ~60s against a fixed 45s abort,
                // so no chunk could ever succeed and each retry resent it all).
                //  - 128 KB chunks: less to resend when a connection drops.
                //  - XHR with an IDLE timeout: abort only when no bytes have
                //    moved for 30s, never just because a chunk is slow.
                //  - Resumable: finished chunks are remembered per file, so a
                //    retry/resubmit continues where it stopped (the server keeps
                //    part files for 2h and accepts repeats idempotently).
                //  - Real byte-level progress + ETA, and stagingCtl.cancel()
                //    stops it (single-flight with a newer submit).
                const chunkSize = 128 * 1024;
                const CHUNK_IDLE_MS = 30000;
                const chunkRefs = {};
                const sha256Hex = async (blob) => {
                    try {
                        if (!window.crypto || !crypto.subtle) return '';
                        const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
                        return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
                    } catch (e) { return ''; }
                };
                const uploadErr = (msg, extra) => Object.assign(new Error(msg), { pgcUploadFailed: true }, extra || {});
                const postChunk = (fd, onLoaded) => new Promise((resolve, reject) => {
                    if (stagingCtl.cancelled) { reject(uploadErr('Upload cancelled', { pgcCancelled: true })); return; }
                    const xhr = new XMLHttpRequest();
                    stagingCtl.xhr = xhr;
                    let idle = 0, stalled = false;
                    const kick = () => {
                        clearTimeout(idle);
                        idle = setTimeout(() => { stalled = true; try { xhr.abort(); } catch (e) {} }, CHUNK_IDLE_MS);
                    };
                    const done = () => { clearTimeout(idle); if (stagingCtl.xhr === xhr) stagingCtl.xhr = null; };
                    xhr.open('POST', '/api/grade-upload-chunk.php', true);
                    xhr.withCredentials = true;
                    xhr.upload.onprogress = (e) => { kick(); if (onLoaded) onLoaded(e.loaded || 0); };
                    xhr.upload.onload = kick; // body sent — now waiting on the reply
                    xhr.onload = () => {
                        done();
                        let data = null;
                        try { data = JSON.parse(xhr.responseText); } catch (e) {}
                        if (xhr.status >= 200 && xhr.status < 300 && data && data.success) resolve(data);
                        else {
                            // Edge/WAF block (e.g. a CF managed rule answering
                            // 403 with an HTML error-1020 page): this endpoint
                            // only ever answers JSON, so a non-JSON 403 means
                            // the request never reached us. Flagged for
                            // dedicated copy below — the generic "connection
                            // dropped" message misleads these users into Wi-Fi
                            // retries that can never succeed (user 5409: five
                            // edge-blocked chunk POSTs, no action row ever).
                            const edgeBlocked = xhr.status === 403 && !data && (
                                String(xhr.getResponseHeader('CF-Mitigated') || '').toLowerCase() === 'challenge'
                                || /^\s*</.test(String(xhr.responseText || '')));
                            reject(uploadErr((data && data.error) || ('Upload failed with HTTP ' + xhr.status),
                                { status: xhr.status, pgcEdgeBlocked: edgeBlocked }));
                        }
                    };
                    xhr.onerror = () => { done(); reject(uploadErr('Upload connection dropped')); };
                    xhr.onabort = () => {
                        done();
                        if (stagingCtl.switching) { reject(uploadErr('Switching to optimized photos', { pgcSwitch: true })); return; }
                        reject(uploadErr(stagingCtl.cancelled ? 'Upload cancelled' : (stalled ? 'Upload stalled — no data moved for 30s' : 'Upload aborted'),
                            { pgcCancelled: stagingCtl.cancelled }));
                    };
                    kick();
                    xhr.send(fd);
                });
                const waitOnline = () => new Promise(res => {
                    if (navigator.onLine !== false) { res(); return; }
                    const t = setTimeout(res, 60000);
                    window.addEventListener('online', () => { clearTimeout(t); res(); }, { once: true });
                });
                // Slow-uplink photo optimization (owner decision 2026-10-22):
                // resize to 2400px long side, JPEG q0.85 — only on uploads
                // measured (or reported) as very slow, and only when it saves
                // at least 30%. Fast connections always send full resolution.
                // The result is cached per original so a retry resumes the
                // same optimized file. Same 2400px cap the Ximilar grade
                // image uses (pgcXimilarGradeImage); <img> decode applies EXIF
                // orientation, so the output is upright.
                const pgcOptimizeForUpload = async (file) => {
                    try {
                        window.__pgcSlowNetFiles = window.__pgcSlowNetFiles || {};
                        const key = (file.name || '') + '|' + file.size + '|' + (file.lastModified || 0);
                        if (window.__pgcSlowNetFiles[key]) return window.__pgcSlowNetFiles[key];
                        if (typeof decodeToDrawable !== 'function') return null;
                        const d = await decodeToDrawable(file);
                        if (!d || !d.w || !d.h) return null;
                        const scale = Math.min(1, 2400 / Math.max(d.w, d.h));
                        const w = Math.round(d.w * scale), h = Math.round(d.h * scale);
                        const c = document.createElement('canvas');
                        c.width = w; c.height = h;
                        c.getContext('2d').drawImage(d.src, 0, 0, w, h);
                        if (d.bitmap && d.bitmap.close) d.bitmap.close();
                        const blob = await new Promise(r => c.toBlob(r, 'image/jpeg', 0.85));
                        if (!blob || blob.size > file.size * 0.7) return null;
                        const out = new File([blob], (file.name || 'photo').replace(/\.\w+$/, '') + '_opt.jpg',
                            { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
                        window.__pgcSlowNetFiles[key] = out;
                        return out;
                    } catch (e) { return null; }
                };
                const stageFile = async (field, file, onProgress, force) => {
                    if (!file || (file.size < chunkMin && !force)) return;
                    const cacheKey = field + '|' + (file.name || '') + '|' + file.size + '|' + (file.lastModified || 0);
                    window.__pgcChunkUploads = window.__pgcChunkUploads || {};
                    let entry = window.__pgcChunkUploads[cacheKey];
                    if (entry && entry.ready && entry.uploadId) {
                        chunkRefs[field] = entry.uploadId;
                        onProgress(file.size, 0);
                        return;
                    }
                    // An unreadable file (cloud-only/revoked Android pick) fails
                    // every chunk instantly and never reaches the server —
                    // detect it once instead of burning 6 retries per attempt.
                    if (typeof file.slice(0, 1).arrayBuffer === 'function') {
                        try { await file.slice(0, Math.min(file.size, chunkSize)).arrayBuffer(); }
                        catch (re) { throw uploadErr('Photo file is not readable', { pgcUnreadable: true }); }
                    }
                    if (!entry || entry.chunkSize !== chunkSize) {
                        entry = {
                            uploadId: 'u' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 14),
                            chunkSize: chunkSize, total: Math.ceil(file.size / chunkSize), done: {}, ready: false,
                            checksum: await sha256Hex(file)
                        };
                        window.__pgcChunkUploads[cacheKey] = entry;
                    }
                    let committed = 0;
                    for (let seq = 0; seq < entry.total; seq++) {
                        const start = seq * chunkSize, end = Math.min(file.size, start + chunkSize);
                        if (entry.done[seq]) { committed += end - start; onProgress(committed, 0); continue; }
                        const fd = new FormData();
                        fd.set('csrf_token', form.querySelector('input[name="csrf_token"]') ? form.querySelector('input[name="csrf_token"]').value : (window.csrfToken || ''));
                        fd.set('upload_id', entry.uploadId);
                        fd.set('field', field);
                        fd.set('seq', String(seq));
                        fd.set('total', String(entry.total));
                        fd.set('size', String(file.size));
                        fd.set('name', file.name || field + '.jpg');
                        fd.set('type', file.type || 'application/octet-stream');
                        if (entry.checksum) fd.set('sha256', entry.checksum);
                        fd.set('chunk', file.slice(start, end, file.type || 'application/octet-stream'), file.name || field + '.bin');
                        let lastErr = null;
                        for (let attempt = 0; attempt < 6; attempt++) {
                            try {
                                await waitOnline();
                                await postChunk(fd, (loaded) => onProgress(committed, Math.min(loaded, end - start)));
                                lastErr = null;
                                break;
                            } catch (e) {
                                lastErr = e;
                                if (e.pgcCancelled || e.pgcSwitch || e.status === 401 || e.status === 403 || e.status === 409 || e.status === 413) break;
                                funnel('grade_chunk_retry', { field: field, seq: seq, attempt: attempt + 1, status: e.status || 0, why: String(e.message || '').slice(0, 60) });
                                await new Promise(r => setTimeout(r, Math.min(8000, 1000 * Math.pow(2, attempt))));
                            }
                        }
                        if (lastErr) throw lastErr;
                        entry.done[seq] = true;
                        committed += end - start;
                        onProgress(committed, 0);
                    }
                    entry.ready = true;
                    chunkRefs[field] = entry.uploadId;
                };
                const fmtMb = (b) => (b / 1048576).toFixed(1);
                const stageChunkUploads = async () => {
                    const staged = [
                        { field: 'image_front', file: selectedFile(frontInput) },
                        { field: 'image_back', file: selectedFile(backInput) }
                    ].filter(x => x.file && x.file.size >= chunkMin).map(x => Object.assign(x, { done: false, force: false }));
                    if (!staged.length) return;
                    const sumBytes = (list) => list.reduce((n, x) => n + x.file.size, 0);
                    let totalBytes = sumBytes(staged);
                    let t0 = Date.now();
                    let base = 0, slowShown = false, lastPaint = 0, startBytes = null, lastEta = 0;
                    let optimized = false;
                    const optimize = async (why) => {
                        if (optimized) return;
                        optimized = true;
                        const beforeKb = Math.round(sumBytes(staged.filter(x => !x.done)) / 1024);
                        for (const it of staged) {
                            if (it.done) continue;
                            const small = await pgcOptimizeForUpload(it.file);
                            if (small) { it.file = small; it.force = true; }
                        }
                        const afterKb = Math.round(sumBytes(staged.filter(x => !x.done)) / 1024);
                        funnel('grade_upload_optimized', { why: why, before_kb: beforeKb, after_kb: afterKb });
                        if (afterKb < beforeKb) {
                            const stEl = document.querySelector('#grading-loading-modal #glm-status');
                            if (stEl) { stEl.style.opacity = '1'; stEl.textContent = 'Slow connection detected — we optimized your photos (' + fmtMb(beforeKb * 1024) + ' → ' + fmtMb(afterKb * 1024) + ' MB) so they send faster. Keep this page open.'; }
                        }
                    };
                    // Data Saver / 2G reported by the browser → optimize up front.
                    const conn = navigator.connection;
                    if (conn && (conn.saveData || /(^|-)2g$/.test(String(conn.effectiveType || '')))) {
                        await optimize('connection');
                        totalBytes = sumBytes(staged);
                    }
                    // Slow uplink: once the upload clearly has >20s to go, swap
                    // the modal's grading copy for honest upload copy (the
                    // countdown is about the grade, which hasn't started yet).
                    // Fast connections keep the normal countdown.
                    const slowCopyTimer = setInterval(() => {
                        if (Date.now() - t0 < 6000 || lastEta <= 20) return;
                        clearInterval(slowCopyTimer);
                        const glm = document.getElementById('grading-loading-modal');
                        if (!glm) return;
                        try { const iv = JSON.parse(glm.dataset.intervals || '{}'); clearInterval(iv.countdown); clearInterval(iv.status); } catch (e) {}
                        const lbl = glm.querySelector('#glm-countdown-label');
                        if (lbl) { lbl.textContent = 'Sending your photos'; lbl.style.color = '#fbbf24'; lbl.style.fontWeight = '700'; }
                        const num = glm.querySelector('#glm-countdown-num');
                        if (num) num.textContent = '…';
                        const stEl = glm.querySelector('#glm-status');
                        if (stEl) { stEl.style.opacity = '1'; stEl.textContent = 'Grading starts as soon as your photos finish sending.'; }
                        glm.dataset.pgcStagingCopy = '1';
                    }, 2000);
                    const onFileProgress = (committed, inFlight) => {
                        const sent = base + committed + inFlight;
                        if (startBytes === null) startBytes = sent;
                        const now = Date.now();
                        if (now - lastPaint < 400 && sent < totalBytes) return;
                        lastPaint = now;
                        const secs = (now - t0) / 1000;
                        const rate = secs > 3 ? (sent - startBytes) / secs : 0;
                        let detail = fmtMb(sent) + ' of ' + fmtMb(totalBytes) + ' MB';
                        if (rate > 0 && sent < totalBytes) {
                            const eta = (totalBytes - sent) / rate;
                            lastEta = eta;
                            detail += eta > 90 ? ' · about ' + Math.ceil(eta / 60) + ' min left' : ' · about ' + Math.max(5, Math.round(eta / 5) * 5) + 's left';
                        }
                        uploadProg(Math.min(0.99, sent / totalBytes), detail);
                        // Measured slow uplink with over a minute still to go:
                        // stop the full-size upload and switch to optimized photos.
                        if (!optimized && !stagingCtl.switching && secs > 8 && rate > 0 && rate < 40 * 1024 && lastEta > 60) {
                            stagingCtl.switching = true;
                            try { if (stagingCtl.xhr) stagingCtl.xhr.abort(); } catch (e) {}
                        }
                        if (!slowShown && secs > 8 && rate > 0 && rate < 40 * 1024) {
                            slowShown = true;
                            funnel('grade_upload_slow', { kbps: Math.round(rate / 1024 * 10) / 10, total_kb: Math.round(totalBytes / 1024) });
                            const stEl = document.querySelector('#grading-loading-modal #glm-status');
                            if (stEl) { stEl.style.opacity = '1'; stEl.textContent = 'Your connection is slow, so sending takes a little longer. Keep this page open — grading starts automatically when the upload finishes.'; }
                        }
                    };
                    try {
                        for (;;) {
                            try {
                                for (const item of staged) {
                                    if (item.done) continue;
                                    await stageFile(item.field, item.file, onFileProgress, item.force);
                                    item.done = true;
                                    base += item.file.size;
                                }
                                break;
                            } catch (e) {
                                if (!e || !e.pgcSwitch) throw e;
                                // Switch to optimized photos for whatever isn't
                                // sent yet; files already completed stay full-size.
                                stagingCtl.switching = false;
                                await optimize('measured');
                                totalBytes = sumBytes(staged);
                                base = sumBytes(staged.filter(x => x.done));
                                t0 = Date.now(); startBytes = null; lastEta = 0;
                            }
                        }
                    } finally {
                        clearInterval(slowCopyTimer);
                    }
                    uploadProg(1);
                    const glm = document.getElementById('grading-loading-modal');
                    if (glm && glm.dataset.pgcStagingCopy) {
                        const lbl = glm.querySelector('#glm-countdown-label');
                        if (lbl) { lbl.textContent = 'Analyzing your card'; lbl.style.color = '#34d399'; }
                        const stEl = glm.querySelector('#glm-status');
                        if (stEl) stEl.textContent = 'Photos received — your card is being graded now. This usually takes about a minute.';
                    }
                };
                try {
                    await stageChunkUploads();
                } catch (uploadErr) {
                    uploadErr.pgcUploadFailed = true;
                    if (uploadErr.pgcEdgeBlocked) funnel('grade_chunk_edge_blocked', { status: uploadErr.status || 403 });
                    uploadErr.pgcUserMessage = uploadErr.pgcCancelled
                        ? ''
                        : uploadErr.pgcUnreadable
                        ? pgcT('up.unreadable', 'Your phone couldn\'t hand this photo over — it\'s probably stored only in the cloud (e.g. Google Photos). No credits were used. Open it in your Photos/Gallery app and download it to the phone first, or use the Camera button to take a new photo.')
                        : uploadErr.pgcEdgeBlocked
                        ? pgcT('up.sec_blocked', 'A security check blocked the upload before it reached grading. Your photos are still selected. Please try again; if this keeps happening, contact support.')
                        : pgcT('up.conn_dropped', 'Your connection dropped while sending the photos, so grading hasn\'t started and no credits were used. The parts already sent are saved — tap Grade again (ideally on Wi-Fi or a stronger signal) and it picks up where it stopped.');
                    throw uploadErr;
                }
                // Photos are staged — from here a job can exist, so the
                // watchdogs and pollers become meaningful (phase gate).
                gradePosted = true;
                postStartMs = Date.now();
                startGradeProgressPoll(sinceSec, postStartMs);
                armWatchdogs();
                const doSubmit = () => {
                    const fd = new FormData(form);
                    // Demo marker (?demo=1) — recorded in the job spec so
                    // demo traffic stays identifiable server-side.
                    if (PGC_GRADE_PROGRESS_DEMO) fd.set('demo', '1');
                    // Swap in browser-converted JPEGs for HEIC originals the
                    // input couldn't replace itself (no DataTransfer support).
                    form.querySelectorAll('input[type="file"][name]').forEach(function (fi) {
                        if (fi._heicFile) fd.set(fi.name, fi._heicFile, fi._heicFile.name);
                    });
                    Object.keys(chunkRefs).forEach(function (field) {
                        fd.delete(field);
                        fd.set(field + '_upload_id', chunkRefs[field]);
                    });
                    return new Promise(function (resolve, reject) {
                        const xhr = new XMLHttpRequest();
                        xhr.open('POST', endpoint, true);
                        xhr.withCredentials = true;
                        // No default XHR timeout — a stalled response stream
            // (h2 reset, proxy hang) would otherwise leave the modal
            // spinning forever. Grades run INLINE (GRADE_JOB_MODE is off), so
            // the response lands when the grade finishes: slowest observed is
            // 196s and FPM terminates at 300s, so 280s sits just under that.
            xhr.timeout = 280000;
                        xhr.upload.onprogress = function (e) {
                            if (e.lengthComputable) uploadProg(e.loaded / e.total);
                        };
                        xhr.upload.onload = function () { uploadProg(1); };
                        xhr.onload = function () {
                            const body = xhr.responseText;
                            resolve({
                                status: xhr.status,
                                ok: xhr.status >= 200 && xhr.status < 300,
                                contentType: xhr.getResponseHeader('Content-Type') || '',
                                cfMitigated: xhr.getResponseHeader('CF-Mitigated') || '',
                                body,
                                json: function () { return Promise.resolve(JSON.parse(body)); },
                                text: function () { return Promise.resolve(body); }
                            });
                        };
                        xhr.onerror = function () { reject(new Error('Network error')); };
                        xhr.ontimeout = function () {
                            const te = new Error('Upload timed out');
                            te.pgcNetStall = true; // job may still be running — recover, don't dead-end
                            reject(te);
                        };
                        xhr.onabort = function () { reject(new Error('Upload aborted')); };
                        xhr.send(fd);
                    });
                };
                try {
                    response = await doSubmit();
                } catch (submitErr) {
                    // Transport failure mid-upload (dropped mobile connection —
                    // server sees UPLOAD_ERR_PARTIAL or nothing at all). A dead
                    // POST never spawned a job, and a completed-POST duplicate
                    // dedupes via the 409/in_flight path — safe to retry once.
                    transportRetried = true;
                    funnel('grade_transport_retry', { attempt: 2, via: 'rejection' });
                    await new Promise(r => setTimeout(r, 1500));
                    response = await doSubmit();
                }

                // 413/408 = body truncated or stalled (iOS suspend) before a job
                // before any job spawned — same safe-retry case as above.
                if ((response.status === 413 || response.status === 408) && !transportRetried) {
                    transportRetried = true;
                    funnel('grade_transport_retry', { attempt: 2, via: String(response.status) });
                    await new Promise(r => setTimeout(r, 1500));
                    response = await doSubmit();
                }

                const edgeChallenge = response.status === 403
                    && (String(response.cfMitigated || '').toLowerCase() === 'challenge'
                        || (/text\/html/i.test(response.contentType || '')
                            && /^\s*(?:<!doctype html|<html\b)/i.test(response.body || '')));
                if (edgeChallenge) {
                    hideGradingModal();
                    window.gradingInProgress = false;
                    delivered = true;
                    deliveredBy = 'fetch';
                    stopRecovery();
                    showAlert(pgcT('up.sec_blocked', 'A security check blocked the upload before it reached grading. Your photos are still selected. Please try again; if this keeps happening, contact support.'), 'error');
                    return;
                }

                // CSRF tokens go stale (session regenerated after signup, or a
                // long-open tab). Refresh the token from a fresh copy of this
                // page and retry once before failing — the upload selection
                // survives, unlike a redirect to /auth/register.
                if (response.status === 403) {
                    try {
                        const html = await (await fetch(location.pathname + location.search, { credentials: 'same-origin' })).text();
                        const m = html.match(/name="csrf_token" value="([a-f0-9]{16,})"/i)
                                || html.match(/window\.csrfToken\s*=\s*"([a-f0-9]{16,})"/);
                        if (m && m[1]) {
                            window.csrfToken = m[1];
                            const tokInput = form.querySelector('input[name="csrf_token"]');
                            if (tokInput) tokInput.value = m[1];
                            csrfRetried = true;
                            response = await doSubmit();
                        }
                    } catch (csrfErr) { /* fall through to normal error handling */ }
                }
                let result = null;
                try {
                    result = await response.json();
                } catch (jsonErr) {
                    // Non-JSON body (an HTML error page from the server, proxy,
                    // or edge — the 2026-10-16 class where every submit returned
                    // "<html>..." and users churned on a cryptic parse error).
                    // Treat it as a terminal, refund-safe failure: tell the user
                    // in plain language, stop any recovery polling, and free the
                    // form. The photos stay staged for an immediate retry.
                    const bodySniff = (response.body || '').toString().slice(0, 80);
                    funnel('grade_client_reject', { reason: 'non_json_response', http_status: response.status, body: bodySniff.slice(0, 40) });
                    hideGradingModal();
                    window.gradingInProgress = false;
                    delivered = true;
                    deliveredBy = 'fetch';
                    stopRecovery();
                    allSubmitBtns.forEach(b => { b.disabled = false; });
                    showAlert('Something went wrong on our end (the grading service returned an error page). Your photos are still selected — please try again. If this keeps happening, contact support.', 'error');
                    return;
                }

                const isSuccess = result.success || result.records || (result.data && result.data.records);
                // Async ack ({status:'processing'}) skips this event — the
                // poll-completion path logs the single grade_response so the
                // funnel doesn't double-count one grade.
                if (result.status !== 'processing') funnel('grade_response', {
                    http_status: response.status,
                    http_status_source: 'transport',
                    grade_outcome: result.noncard ? 'noncard_reject' : (result.retake ? 'photo_reject' : (isSuccess ? 'completed' : 'request_error')),
                    action_id: result.action_id || (result.data && result.data.action_id) || null,
                    success: isSuccess ? 1 : 0,
                    error: isSuccess ? '' : String(result.error || '').slice(0, 120),
                    retake: result.retake ? 1 : 0,
                    csrf_retried: csrfRetried ? 1 : 0,
                    transport_retried: transportRetried ? 1 : 0
                });

                if (result.status === 'processing') {
                    // Async pipeline ack — the server is grading detached and
                    // persisted the ack in seconds. Poll grade-status for the
                    // terminal state; the modal/countdown keep running and the
                    // poll handler delivers done/failed/reject.
                    startRecoveryPolling('poll');
                } else if (delivered && deliveredBy !== 'poll_empty' && deliveredBy !== 'poll_timeout') {
                    // A recovery poll already rendered a terminal outcome —
                    // the late response is a no-op (telemetry above still fired).
                    // poll_empty/poll_timeout are inconclusive ("nothing found
                    // yet"), so a real response that lands after them still
                    // wins below — the fetch is the source of truth.
                } else if (isSuccess) {
                    if (delivered) funnel('grade_empty_superseded', { outcome: 'success', polls: 0 });
                    delivered = true;
                    deliveredBy = 'fetch';
                    stopRecovery();
                    handleGradeSuccess(result);
                } else {
                    hideGradingModal();
                    window.gradingInProgress = false;
                    if (response.status === 409 || result.in_flight) {
                        // Duplicate submit while a grade is already running
                        // (second tab, replayed submit, retry after a dropped
                        // connection) — the real result is still coming.
                        // Poll for it instead of dead-ending on the alert.
                        startRecoveryPolling('in_flight');
                        showAlert(result.error || 'A grading is already in progress — waiting for it to finish.', 'warning');
                    } else {
                        // Definitive non-success — a stall-started poller must
                        // not later fire a confusing "check history" toast over
                        // the real error/upsell this response carries. If an
                        // inconclusive empty/timeout poll already did, this real
                        // response replaces it (the fetch is the source of truth).
                        if (delivered) funnel('grade_empty_superseded', { outcome: 'error', http_status: response.status, retake: result.retake ? 1 : 0 });
                        delivered = true;
                        deliveredBy = 'fetch';
                        stopRecovery();
                        if (response.status === 402 && window.CreditUpsell) {
                            // A 402 while an earlier grade attempt is pending
                            // recovery means the in-flight/completed grade just
                            // consumed the balance — an "insufficient credits"
                            // upsell here is a false alarm mid-flight. Suppress
                            // it; the recovery poller will deliver the result.
                            if (window.pgcGradePending) {
                                funnel('grade_gate_suppressed_inflight', { http_status: 402 });
                                showAlert('Your grade is still processing — the result will appear automatically. No additional credits were charged.', 'info');
                            } else {
                                // Stash the photo(s) — a paid return restores
                                // them so the user doesn't re-photograph, and
                                // the upsell flag lets the paid return
                                // auto-resubmit (the user just paid to finish
                                // THIS grade).
                                pgcStashPendingScan(frontInput, backInput, true);
                                window.CreditUpsell.show(result.credits_required || 1, result.credits_available || 0);
                            }
                        } else if (result.retake || result.noncard) {
                            // Photo rejected before grading (cut-off card, no card
                            // detected, known non-card, etc.) — show the rejection
                            // modal instead of a toast.
                            showUploadRejectedModal(result, previewUrl);
                        } else {
                            showAlert(result.error || (PGC_XIM_SLOW
                                ? 'Our grading provider is having trouble right now — not your photo. Any credits used were refunded; please try again in a few minutes.'
                                : 'Analysis failed'), 'error');
                            if (response.status === 401 || response.status === 403) {
                                // Session expired mid-flow — send to login (not
                                // register) with a return path back to this page.
                                // Login redirects straight back if still logged in.
                                setTimeout(() => {
                                    window.location.href = result.redirect || ('/auth/login?redirect=' + encodeURIComponent(location.pathname + location.search));
                                }, 1500);
                            }
                        }
                    }
                }
            } catch (err) {
                // Superseded by a newer submit (single-flight): that attempt
                // owns the modal, buttons and wake lock — leave them alone.
                if (err && err.pgcCancelled) { supersededQuietly = true; return; }
                const elapsedMs = Date.now() - submitStartMs;
                funnel('grade_response', {
                    http_status: response ? response.status : 0,
                    http_status_source: 'transport',
                    grade_outcome: 'client_exception',
                    action_id: null,
                    success: 0,
                    error: 'client_exception',
                    detail: String(err && err.message || err).slice(0, 160),
                    elapsed_ms: elapsedMs,
                    csrf_retried: csrfRetried ? 1 : 0,
                    transport_retried: transportRetried ? 1 : 0
                });
                hideGradingModal();
                // Network-layer drops (Safari "Load failed", Chrome "Failed to
                // fetch") and malformed-body parse errors usually mean the
                // server is STILL grading — the result lands in history and
                // credits settle normally. Poll for the completed result so
                // it renders automatically instead of inviting a blind
                // resubmit that would double-bill. gradingInProgress stays true
                // here: the server lock may still be held, and a tap on the
                // re-enabled-looking form would just 409.
                const netDrop = PGC_NET_DROP_RE.test(String(err && err.message || ''))
                    || (err instanceof SyntaxError)
                    || (err && err.pgcNetStall);
                if (err && err.pgcUploadFailed) {
                    window.gradingInProgress = false;
                    delivered = true;
                    deliveredBy = 'fetch';
                    stopRecovery();
                    showAlert(err.pgcUserMessage || ('Upload error: ' + err.message), 'error');
                } else if (netDrop) {
                    startRecoveryPolling('fetch_drop');
                    showAlert('The connection dropped, but your grade is likely still processing — we\'ll keep checking and show the result automatically when it lands.', 'warning');
                } else {
                    window.gradingInProgress = false;
                    delivered = true;
                    deliveredBy = 'fetch';
                    stopRecovery();
                    showAlert('Connection error: ' + err.message, 'error');
                }
            } finally {
                clearTimeout(stallTimer);
                clearTimeout(stallLogTimer);
                if (window.__pgcStaging === stagingCtl) window.__pgcStaging = null;
                if (!supersededQuietly) {
                    pgcWakeLock.release();
                    if (!window.pgcGradePending) allSubmitBtns.forEach(b => { b.disabled = false; });
                }
            }
        });
    }

    // Back add-on affordability: flag it on the upload zone the moment a back
    // photo lands, not after the grade completes. Covers picker, drag-drop,
    // camera and HEIC paths — programmatic input.files assignments don't fire
    // 'change', so the zone's has-file class flip is the reliable signal.
    const backZone = document.getElementById('upload-zone-back');
    const backFileInput = backZone ? backZone.querySelector('input[name="image_back"]') : null;
    if (backZone && backFileInput) {
        const updateBackCreditNote = () => {
            const existing = document.getElementById('back-addon-credit-note');
            const hasBack = !!(backFileInput.files && backFileInput.files.length) || !!backFileInput._heicFile;
            const bc = parseFloat(window.GRADE_BACK_CREDITS) || 0;
            const gc = parseFloat(window.GRADE_CREDITS) || 1;
            const bal = parseFloat(window.CREDIT_BALANCE) || 0;
            const unaffordable = window.IS_LOGGED_IN && bc > 0 && bal < gc + bc;
            if (hasBack && unaffordable) {
                if (existing) return;
                const note = document.createElement('div');
                note.id = 'back-addon-credit-note';
                note.style.cssText = 'margin-top:10px;padding:10px 14px;border-radius:10px;background:#fffbeb;border:1px solid #fcd34d;color:#92400e;font-size:12px;line-height:1.5;text-align:center;';
                note.innerHTML = 'Your balance (<strong>' + bal + ' credit' + (bal === 1 ? '' : 's') + '</strong>) can\'t cover the +' + bc + ' back add-on — it will be skipped this grade. <a href="/dashboard/credits" style="color:#b45309;font-weight:700;text-decoration:underline;">Add credits</a>';
                // Zone click opens the file picker — keep the note (and its link) from triggering it.
                note.addEventListener('click', (e) => e.stopPropagation());
                backZone.appendChild(note);
            } else if (existing) {
                existing.remove();
            }
        };
        backFileInput.addEventListener('change', updateBackCreditNote);
        new MutationObserver(updateBackCreditNote).observe(backZone, { attributes: true, attributeFilter: ['class'] });
    }

    // Staged-photo stash for ordinary picks (2026-10-27): Android kills the
    // backgrounded tab while the OS file picker or camera is open, then
    // reloads on return — staged files die silently (u5074: page reloaded
    // mid-picker; he re-staged the front, the back was gone, submitted
    // has_back:0). The has-file class flip is the reliable "a file
    // landed/left" signal across picker, camera, HEIC and remove paths, so
    // each flip re-syncs the stash; pgcRestorePendingScan re-attaches on the
    // next load. The 402/photo-check stashes stay untouched —
    // pgcStashPendingScan owns those writes (upsell/handoff flags).
    (function () {
        const stashForm = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
        if (!stashForm || !window.indexedDB) return;
        const frontInp = stashForm.querySelector('input[name="image_front"]');
        const backInp = stashForm.querySelector('input[name="image_back"]');
        if (!frontInp && !backInp) return;
        let stashT = null;
        const syncStash = () => {
            clearTimeout(stashT);
            stashT = setTimeout(() => {
                const f = frontInp ? pgcStagedFile(frontInp) : null;
                const b = backInp ? pgcStagedFile(backInp) : null;
                pgcPendingScanStore('readwrite').then(st => {
                    if (!f && !b) { st.delete('latest'); return; }
                    st.put({ front: f || null, back: b || null,
                             frontSize: f ? f.size : 0, backSize: b ? b.size : 0,
                             ts: Date.now(), path: location.pathname }, 'latest');
                }).catch(() => {});
            }, 250);
        };
        ['upload-zone-front', 'upload-zone-back'].forEach(id => {
            const z = document.getElementById(id);
            if (z) new MutationObserver(syncStash).observe(z, { attributes: true, attributeFilter: ['class'] });
        });
    })();
});

function displayResults(data, endpoint, container, meta) {
    if (!container) {
        // No report mount on this page — still deliver the persisted report
        // instead of silently dropping the result.
        const rec0 = (data.records && data.records[0]) || {};
        const aid0 = data.action_id || (meta && meta.action_id) || null;
        if (aid0 && (rec0.grades || rec0.centering || rec0.card)) {
            window.location.href = location.pathname + '?result=' + encodeURIComponent(aid0) + '#xim-report-top';
        }
        return;
    }
    if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
        console.log('Grade V3 API response:', data);
    }
    const record = (data.records && data.records[0]) || {};

    // Update global credit balance from API response so post-result upsell shows correct value
    var newBalance = data.credits_remaining;
    if ((newBalance === undefined || newBalance === null) && meta) {
        newBalance = meta.credits_remaining;
    }
    if (newBalance !== undefined && newBalance !== null) {
        window.CREDIT_BALANCE = newBalance;
    }

    var actionId = data.action_id || (meta && meta.action_id) || null;
    if (actionId && (record.grades || record.centering || record.card)) {
        // The action_history row is the render source — reload this page
        // with ?result= so PHP emits the report server-side (same renderer
        // as history-detail and /r/ share pages). Derived images, comps,
        // economics — everything was persisted before the row was written.
        container.innerHTML = '<div style="padding:48px 20px;text-align:center;color:#64748b;font-weight:600;">Grade complete — loading your report…</div>';
        return location.pathname + '?result=' + encodeURIComponent(actionId) + '#xim-report-top';
    }

    if (record.grades || record.centering || record.card) {
        // No persisted action id (shouldn't happen for a real grade) —
        // render what the response carries as a fallback.
        const renderMeta = Object.assign({}, meta || {}, {
            action_id: data.action_id || null,
            share_url: data.share_url || (meta && meta.share_url) || null,
            share_token: data.share_token || (meta && meta.share_token) || null
        });
        var creditsUsed = data.credits_used;
        if (creditsUsed == null && meta && meta.credits_used != null) {
            creditsUsed = meta.credits_used;
        }
        container.innerHTML = XimilarResults.render('grade', record, data.original_image_url, creditsUsed, data.statistics, renderMeta);
        if (typeof window.pgcWatchResultView === 'function') window.pgcWatchResultView(container);
        return;
    }

    container.innerHTML = '<div style="padding: 20px; background: #f0f0f0;">Data received but no grade analysis available</div>';
}

function showAlert(msg, type) {
    const div = document.createElement('div');
    div.className = 'pgc-alert';
    const isError = type === 'error';
    const bg = isError ? '#fe2c55' : (type === 'warning' ? '#d97706' : '#10b981');
    div.style.cssText = `position:fixed; bottom:30px; right:30px; max-width:420px; background:${bg}; color:white; padding:18px 48px 18px 32px; border-radius:16px; z-index:999999; font-weight:700; font-size: 14px; line-height:1.5; box-shadow:0 15px 30px rgba(0,0,0,0.3); transform: translateY(0); transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);`;
    div.textContent = msg;

    // Close button (always present, but errors stay until dismissed)
    const closeBtn = document.createElement('span');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = 'position:absolute; top:10px; right:14px; font-size:22px; font-weight:300; cursor:pointer; opacity:0.8; line-height:1; padding:2px 4px;';
    closeBtn.onmouseover = () => closeBtn.style.opacity = '1';
    closeBtn.onmouseout = () => closeBtn.style.opacity = '0.8';
    closeBtn.onclick = () => {
        div.style.opacity = '0';
        div.style.transform = 'translateY(20px)';
        setTimeout(() => div.remove(), 400);
    };
    div.appendChild(closeBtn);

    document.body.appendChild(div);
}

// Shared grade-status fetch. Two pollers watch every grade — the modal's
// progress tick (3s) and the result-recovery poll (5s) — and each used to
// fetch independently: ~32 requests/min per grading user and ~60 per grade
// (3.8k/day for ~63 grades, 2026-11-02). Both now share one request: a call
// within 2.9s of the last one (or while one is in flight) reuses its result.
// Hidden tabs poll at most every 15s — nobody is watching the spinner.
const PGC_GS = { cache: {} };
// notBefore: never reuse a response whose fetch STARTED before this time. The
// recovery poll passes its own start — a progress tick fired as the modal
// opened (before the grade POST registered server-side) answers "nothing
// running", and reusing it made the recovery poll declare "that grade didn't
// start" while the grade was actually running (regression caught 2026-11-02).
function pgcGradeStatusGet(since, via, notBefore) {
    const key = String(since);
    const now = Date.now();
    const c = PGC_GS.cache[key];
    const minGap = document.hidden ? 15000 : 2900;
    if (c && c.startAt >= (notBefore || 0) && (c.pending || now - c.at < minGap)) return c.promise;
    const entry = { pending: true, at: now, startAt: now, promise: null };
    const ac = window.AbortController ? new AbortController() : null;
    const t = ac ? setTimeout(function () { try { ac.abort(); } catch (e) {} }, 15000) : 0;
    entry.promise = fetch('/api/grade-status.php?since=' + encodeURIComponent(since) + '&via=' + encodeURIComponent(via || 'poll'),
            { credentials: 'same-origin', cache: 'no-store', signal: ac ? ac.signal : undefined })
        .then(function (r) {
            return r.json().then(function (j) { return { status: r.status, ok: r.ok, body: j }; },
                                 function () { return { status: r.status, ok: false, body: null }; });
        })
        .finally(function () { if (t) clearTimeout(t); entry.pending = false; entry.at = Date.now(); });
    PGC_GS.cache[key] = entry;
    return entry.promise;
}

// Localized string from window.PGC_T (footer emits the active locale's `js`
// map) with the English fallback; %s args filled in order. Works for every
// locale — not just es.
function pgcT(key, en) {
    const T = window.PGC_T || {};
    const s = (typeof T[key] === 'string' && T[key]) ? T[key] : en;
    const args = Array.prototype.slice.call(arguments, 2);
    let i = 0;
    return s.replace(/%s/g, function () { return i < args.length ? String(args[i++]) : ''; });
}

// Deferred delivery (#203410): the AI provider's queue is backed up, so the
// grade is waiting server-side (credit held) and will land in history + email
// + bell. A real panel — not a toast that vanishes — so the user knows the
// grade is safe and they can leave or keep grading.
function showDeferredPanel(st) {
    const existing = document.getElementById('pgc-deferred-panel');
    if (existing) existing.remove();
    const capH = (st && st.cap_h) ? Math.round(Number(st.cap_h)) : 6;
    const overlay = document.createElement('div');
    overlay.id = 'pgc-deferred-panel';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'pgc-deferred-title');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:1000000;background:rgba(15,23,42,0.72);display:flex;align-items:center;justify-content:center;padding:16px;';
    const card = document.createElement('div');
    card.style.cssText = 'background:#ffffff;color:#0f172a;max-width:460px;width:100%;border-radius:16px;padding:28px 26px 22px;box-shadow:0 24px 60px rgba(0,0,0,0.35);font-family:inherit;text-align:center;';
    card.innerHTML =
        '<div aria-hidden="true" style="width:56px;height:56px;margin:0 auto 14px;border-radius:50%;background:#eff6ff;display:flex;align-items:center;justify-content:center;">'
        + '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1a468e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></div>'
        + '<h2 id="pgc-deferred-title" style="margin:0 0 10px;font-size:20px;font-weight:800;line-height:1.3;color:#0f172a;"></h2>'
        + '<p data-k="body" style="margin:0 0 10px;font-size:14px;line-height:1.6;color:#334155;"></p>'
        + '<p data-k="note" style="margin:0 0 20px;font-size:13px;line-height:1.5;color:#64748b;"></p>'
        + '<div style="display:flex;gap:10px;justify-content:center;flex-wrap:wrap;">'
        + '<button type="button" data-k="again" style="height:42px;padding:0 18px;border-radius:8px;border:none;background:#22c55e;color:#ffffff;font-weight:700;font-size:14px;cursor:pointer;"></button>'
        + '<a data-k="history" href="/dashboard/history" style="height:42px;padding:0 18px;border-radius:8px;border:1px solid #cbd5e1;background:#ffffff;color:#1e293b;font-weight:700;font-size:14px;text-decoration:none;display:inline-flex;align-items:center;"></a>'
        + '</div>';
    if (st && st.variant === 'slow') {
        // Client-side soft wait: no server deferral yet, so no email/refund-cap promises.
        card.querySelector('#pgc-deferred-title').textContent = pgcT('slow.title', 'Your grade is taking longer than usual');
        card.querySelector('[data-k="body"]').textContent = pgcT('slow.body', 'It\'s still being processed. You can keep this page open — we\'ll show a notice the moment it\'s ready — or come back later: the finished grade will be in your grading history.');
        card.querySelector('[data-k="note"]').textContent = pgcT('slow.note', 'No need to resubmit. If it can\'t be completed, any credits used are refunded automatically.');
    } else {
        card.querySelector('#pgc-deferred-title').textContent = pgcT('defer.title', 'Your grade is queued — we\'ll email you');
        card.querySelector('[data-k="body"]').textContent = pgcT('defer.body', 'Our AI grading provider is running slow right now, so your card is waiting in its queue. You don\'t need to stay on this page — your result will appear in your grading history and we\'ll email you the moment it\'s ready (usually within minutes).');
        card.querySelector('[data-k="note"]').textContent = pgcT('defer.note', 'No need to resubmit. If it can\'t be completed within %s hours, your credit is refunded automatically.', capH);
    }
    const again = card.querySelector('[data-k="again"]');
    again.textContent = pgcT('defer.again', 'Grade another card');
    card.querySelector('[data-k="history"]').textContent = pgcT('defer.history', 'View grading history');
    const close = function () { overlay.remove(); document.removeEventListener('keydown', onKey); };
    const onKey = function (e) { if (e.key === 'Escape') close(); };
    again.addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    try { again.focus(); } catch (e) {}
}

// A grade that finished after the modal closed (soft wait): a persistent,
// dismissible notice with a direct link — not an auto-render that could
// clobber whatever the user is doing on the page now.
function showGradeReadyNotice(actionId) {
    const old = document.getElementById('pgc-grade-ready');
    if (old) old.remove();
    const deferredPanel = document.getElementById('pgc-deferred-panel');
    if (deferredPanel) deferredPanel.remove();
    const box = document.createElement('div');
    box.id = 'pgc-grade-ready';
    box.setAttribute('role', 'status');
    box.style.cssText = 'position:fixed;bottom:24px;right:24px;z-index:999999;max-width:380px;background:#0f172a;color:#ffffff;border-radius:14px;padding:16px 44px 16px 18px;box-shadow:0 16px 36px rgba(0,0,0,0.35);font-size:14px;line-height:1.5;';
    const msg = document.createElement('div');
    msg.style.cssText = 'font-weight:700;margin:0 0 10px;';
    msg.textContent = pgcT('ready.title', 'Your card grade is ready');
    const link = document.createElement('a');
    link.href = actionId ? '/dashboard/history/' + encodeURIComponent(actionId) : '/dashboard/history';
    link.textContent = pgcT('ready.view', 'View your grade →');
    link.style.cssText = 'display:inline-block;background:#22c55e;color:#ffffff;font-weight:700;padding:8px 14px;border-radius:8px;text-decoration:none;';
    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', pgcT('ready.dismiss', 'Dismiss'));
    close.textContent = '×';
    close.style.cssText = 'position:absolute;top:8px;right:10px;background:none;border:none;color:#cbd5e1;font-size:22px;line-height:1;cursor:pointer;padding:2px 6px;';
    close.addEventListener('click', function () { box.remove(); });
    box.appendChild(msg); box.appendChild(link); box.appendChild(close);
    document.body.appendChild(box);
}

// Photo rejected before grading — mirrors the failed-uploads admin display:
// shows the rejected image alongside the reason so the user knows what to fix.
function showUploadRejectedModal(result, previewUrl) {
    const existing = document.getElementById('upload-rejected-modal');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'upload-rejected-modal';
    overlay.style.cssText = 'position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.7); backdrop-filter:blur(8px); z-index:99999; display:flex; align-items:center; justify-content:center; padding:clamp(8px,2vh,20px);';

    const card = document.createElement('div');
    card.style.cssText = 'background:white; border-radius:20px; padding:0; max-width:440px; width:100%; text-align:center; box-shadow:0 25px 60px rgba(0,0,0,0.3); max-height:calc(100vh - 16px); max-height:calc(100dvh - 16px); display:flex; flex-direction:column; overflow:hidden; position:relative;';
    card.setAttribute('data-flex-nowrap', '');

    // Prefer the server-saved photo (the exact image the grader analyzed and
    // admin sees) over the client-side blob preview of the raw file.
    const shownUrl = result.image_url || previewUrl;
    let imgHtml = '';
    if (shownUrl) {
        let svgOverlay = '';
        if (result.detected_polygon && result.image_width && result.image_height) {
            const pts = result.detected_polygon.map(function(p) { return p[0] + ',' + p[1]; }).join(' ');
            const w = result.image_width, h = result.image_height;
            svgOverlay = '<svg viewBox="0 0 ' + w + ' ' + h + '" preserveAspectRatio="none" style="position:absolute; top:0; left:0; width:100%; height:100%; pointer-events:none;">'
                + '<rect x="' + (w * 0.03) + '" y="' + (h * 0.03) + '" width="' + (w * 0.94) + '" height="' + (h * 0.94) + '" fill="none" stroke="#16a34a" stroke-width="2" stroke-dasharray="10 8" vector-effect="non-scaling-stroke" opacity="0.8"/>'
                + '<polygon points="' + pts + '" fill="rgba(220,38,38,0.15)" stroke="#dc2626" stroke-width="3" vector-effect="non-scaling-stroke"/>'
                + '</svg>';
        }
        imgHtml = '<div style="background:#f1f5f9; border-radius:12px; padding:clamp(6px,1.5vh,10px); margin-bottom:6px;"><div style="position:relative; display:inline-block; max-width:100%; margin:0 auto;">'
            + '<img src="' + shownUrl + '" alt="Rejected photo" onerror="this.closest(\'div\').parentNode.style.display=\'none\'" style="max-width:100%; max-height:200px; max-height:min(200px,26dvh); border-radius:8px; display:block;">'
            + svgOverlay
            + '</div></div>'
            + (svgOverlay ? '<p style="color:#94a3b8; font-size:0.72rem; margin:0 0 clamp(6px,1.4vh,10px);">Red outline = what the AI detected as your card. Anything outside the green dashed margin is cut off.</p>' : '');
    }

    const sides = (result.clipped_sides && result.clipped_sides.length)
        ? '<div style="display:flex; flex-wrap:wrap; gap:6px; justify-content:center; margin-bottom:clamp(8px,1.8vh,16px);">'
          + result.clipped_sides.map(function(s) {
              return '<span style="background:#fef2f2; color:#b91c1c; border:1px solid #fecaca; border-radius:999px; padding:4px 12px; font-size:0.78rem; font-weight:700;">' + s.charAt(0).toUpperCase() + s.slice(1) + ' edge cut off</span>';
            }).join('')
          + '</div>'
        : '';

    // Non-card collectible detected (coin, banknote, stamp, comic) — point the
    // user at the right tool instead of a generic "retake" dead end.
    const detectedHtml = result.detected_item
        ? '<div style="background:#eef2ff; color:#4338ca; border:1px solid #c7d2fe; border-radius:999px; padding:4px 14px; font-size:0.78rem; font-weight:700; display:inline-block; margin-bottom:clamp(8px,1.8vh,16px);">Detected: ' + result.detected_item + '</div>'
        : '';

    const tipsHtml = (result.retake_tips && result.retake_tips.length)
        ? '<div style="text-align:left; background:#f0f9ff; border:1px solid #bae6fd; border-radius:12px; padding:clamp(10px,2vh,14px) 14px; margin-bottom:clamp(8px,1.8vh,16px);">'
          + '<div style="font-size:0.78rem; font-weight:800; color:#0369a1; text-transform:uppercase; letter-spacing:0.05em; margin-bottom:clamp(4px,1vh,8px);">How to fix it</div>'
          + '<ul style="margin:0; padding-left:18px; color:#334155; font-size:0.85rem; line-height:1.55;">'
          + result.retake_tips.map(function(t) { return '<li>' + t + '</li>'; }).join('')
          + '</ul></div>'
        : '';

    // Touch users retake with the camera, desktop users pick a file — the CTA
    // row mirrors that (the .use-camera-btn platform rules handle the rest).
    var isCoarse = false;
    try { isCoarse = !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches); } catch (e) {}

    card.innerHTML = ''
        + '<div style="flex:1 1 auto; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; overscroll-behavior:contain; padding:clamp(16px,3.5vh,30px) clamp(18px,5vw,30px) 0;">'
        + '<div style="width:clamp(42px,9vh,64px); height:clamp(42px,9vh,64px); background:#fef2f2; border-radius:50%; display:flex; align-items:center; justify-content:center; margin:0 auto clamp(8px,2vh,16px);">'
        +   '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2" stroke-linecap="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M15 3v18"/><path d="M18 8l3-3M18 16l3 3"/></svg>'
        + '</div>'
        + '<h2 style="font-size:clamp(1.15rem,2.8vh,1.4rem); font-weight:800; color:#1e293b; margin:0 0 clamp(6px,1.2vh,10px);">' + (result.retake_title || 'Card Is Cut Off') + '</h2>'
        + imgHtml
        + detectedHtml
        + sides
        + '<p style="color:#64748b; font-size:clamp(0.85rem,1.9vh,0.92rem); line-height:1.55; margin:0 0 clamp(8px,1.8vh,16px);">'
        +   (result.error || 'Part of the card is outside the photo and could not be evaluated. Please retake the photo with the entire card inside the frame — all four edges and corners visible.')
        + '</p>'
        + tipsHtml
        + '</div>'
        + '<div style="flex:0 0 auto; padding:clamp(8px,1.8vh,14px) clamp(18px,5vw,30px) clamp(14px,3vh,26px); background:#fff; border-top:1px solid #f1f5f9;">'
        + (result.noncard
            ? '<p style="color:#94a3b8; font-size:0.8rem; margin:0 0 clamp(6px,1.4vh,12px);">'
              + (result.charged
                  ? 'This submission used your credits — only trading cards can be graded.'
                  : 'No credit was used — but submitting a non-card photo again will use them.')
              + '</p>'
            : '<p style="color:#94a3b8; font-size:0.8rem; margin:0 0 clamp(6px,1.4vh,12px);">No credit was used — retake the photo and try again.</p>')
        + (result.noncard
            // Known non-card: no retake coaching — either route them to the
            // right tool for the detected item, or just close.
            ? '<button id="upload-rejected-close" style="display:block; width:100%; background:#2563eb; color:white; padding:clamp(11px,2vh,14px); border-radius:12px; font-weight:800; font-size:1rem; border:none; cursor:pointer;">'
              + (result.suggest_url && result.suggest_label ? 'Go to the ' + result.suggest_label + ' →' : 'Got It')
              + '</button>'
            : (isCoarse
            // Touch: camera first — it's what retakers actually reach for
            // (file_picker_cancel → camera_open_attempt pattern, 2026-10-25).
            ? '<button id="upload-rejected-camera" style="display:block; width:100%; background:#2563eb; color:white; padding:clamp(11px,2vh,14px); border-radius:12px; font-weight:800; font-size:1rem; border:none; cursor:pointer; margin-bottom:8px;">📷 Take Photo</button>'
            + '<button id="upload-rejected-close" style="display:block; width:100%; background:#fff; color:#2563eb; padding:clamp(9px,1.7vh,12px); border-radius:12px; font-weight:700; font-size:0.92rem; border:1px solid #bfdbfe; cursor:pointer;">Choose Photo</button>'
            : '<button id="upload-rejected-close" style="display:block; width:100%; background:#2563eb; color:white; padding:clamp(11px,2vh,14px); border-radius:12px; font-weight:800; font-size:1rem; border:none; cursor:pointer;">' + (result.suggest_url ? 'Retake a Card Photo' : 'Retake Photo') + '</button>'))
        + '</div>';

    overlay.appendChild(card);
    document.body.appendChild(overlay);

    // Desktop got a single Retake button — the only close path was the
    // backdrop click, which nothing advertised. A visible × dismisses the
    // same way (touch keeps its two real CTAs and doesn't need a third).
    if (!isCoarse) {
        const xBtn = document.createElement('button');
        xBtn.type = 'button';
        xBtn.setAttribute('aria-label', 'Close');
        xBtn.innerHTML = '&times;';
        xBtn.style.cssText = 'position:absolute; top:10px; right:12px; width:32px; height:32px; border:none; border-radius:50%; background:rgba(241,245,249,0.9); color:#64748b; font-size:22px; font-weight:400; line-height:1; cursor:pointer; z-index:2; padding:0;';
        xBtn.onmouseover = function() { xBtn.style.background = '#e2e8f0'; xBtn.style.color = '#1e293b'; };
        xBtn.onmouseout = function() { xBtn.style.background = 'rgba(241,245,249,0.9)'; xBtn.style.color = '#64748b'; };
        xBtn.addEventListener('click', function() { closeRejectedModal('dismiss'); });
        card.appendChild(xBtn);
    }

    if (typeof pgcGradeEvent === 'function') pgcGradeEvent('photo_reject_shown', {
        retake_title: String(result.retake_title || '').slice(0, 60),
        scan_like: result.scan_like ? 1 : 0,
        noncard: result.noncard ? 1 : 0,
        charged: result.charged ? 1 : 0,
        side: result.side || 'front',
    });

    // Promo surfaces can render over this modal — hide them while the
    // rejection is up, restore on close.
    var hiddenPromos = [];
    document.querySelectorAll('.pgc-promo').forEach(function(el) {
        hiddenPromos.push([el, el.style.display || '']);
        el.style.display = 'none';
    });
    function closeRejectedModal(action) {
        overlay.remove();
        hiddenPromos.forEach(function(pair) { pair[0].style.display = pair[1]; });
        if (typeof pgcGradeEvent === 'function') pgcGradeEvent('photo_reject_action', { choice: action || 'dismiss' });
        // Non-card suggest CTA — route to the right tool (Coin Identifier,
        // Banknote Identifier) instead of reopening the picker.
        if (action === 'goto' && result.suggest_url) { window.location.href = result.suggest_url; return; }
        // Clear the side the reject was about (back-photo rejects exist too —
        // marker payloads carry side:'back') so the known-bad image can't be
        // resubmitted, and land the user at that zone with the CTA in view.
        const zone = document.getElementById(result.side === 'back' ? 'upload-zone-back' : 'upload-zone-front')
            || document.getElementById('upload-zone-front');
        resetUploadZone(zone);
        const form = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
        if (form && typeof syncSubmitButtons === 'function') syncSubmitButtons(form);
        if (zone) zone.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // The button SAYS "Retake Photo" — deliver one. The click carries user
        // activation, so the picker/camera sheet opens directly instead of
        // leaving the user at an empty zone (the reject→bare-Grade-tap→
        // "no photo" loop, u4618). Backdrop dismissals skip it.
        if (!action || action === 'dismiss' || !zone) return;
        const fi = zone.querySelector('input[type="file"]');
        if (action === 'camera') {
            // Prefer the zone's own Camera button — it carries the tested
            // platform logic (capture=environment, Guided Scan, funnel).
            const camBtn = zone.querySelector('.use-camera-btn');
            if (camBtn && camBtn.offsetParent !== null) { camBtn.click(); return; }
            // Button hidden/absent (admin toggle off): force capture= on the
            // input so touch platforms open the camera anyway.
            if (fi) {
                try {
                    fi.setAttribute('capture', 'environment');
                    fi.click();
                    var clearCap = function() { fi.removeAttribute('capture'); };
                    fi.addEventListener('change', clearCap, { once: true });
                    fi.addEventListener('cancel', clearCap, { once: true });
                    // Safari may skip 'cancel' — the zone's own handler uses the
                    // same focus fallback so the next tap isn't camera-locked.
                    window.addEventListener('focus', function onFocus() {
                        setTimeout(clearCap, 400);
                    }, { once: true });
                } catch (e) {}
            }
            return;
        }
        if (action === 'picker') { try { if (fi) fi.click(); } catch (e) {} }
    }

    document.getElementById('upload-rejected-close').addEventListener('click', function () {
        closeRejectedModal(result.noncard ? (result.suggest_url ? 'goto' : 'dismiss') : 'picker');
    });
    var camBtn2 = document.getElementById('upload-rejected-camera');
    if (camBtn2) camBtn2.addEventListener('click', function () { closeRejectedModal('camera'); });
    overlay.addEventListener('click', function(e) { if (e.target === overlay) closeRejectedModal('dismiss'); });
}

// Transport-drop detector for the grade-submit catch — each browser names
// the same failure differently (Chrome 'Failed to fetch', Safari 'Load
// failed', Firefox 'NetworkError when attempting to fetch resource.',
// WKWebView 'The network connection was lost').
const PGC_NET_DROP_RE = /load failed|failed to fetch|network ?error|the network connection was lost/i;

// iOS-family UA (incl. iPadOS desktop-mode "Macintosh" with touch) — mobile
// Safari suspends a backgrounded tab's network mid-upload, and the server
// sees UPLOAD_ERR_PARTIAL. Used to surface the keep-page-open hint.
const PGC_IOS_UA = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);

// Screen Wake Lock — hold the display awake for the upload window so iOS
// Safari can't suspend the tab mid-POST. iOS 16.4+; silent no-op elsewhere.
// Held only around the submit fetch — post-upload result polling recovers
// fine from a suspended tab and doesn't need the screen on.
const pgcWakeLock = {
    _sentinel: null,
    acquire: function () {
        try {
            if ('wakeLock' in navigator && !this._sentinel) {
                var self = this;
                navigator.wakeLock.request('screen').then(function (s) {
                    self._sentinel = s;
                    s.addEventListener('release', function () { self._sentinel = null; });
                }).catch(function () {});
            }
        } catch (e) {}
    },
    release: function () {
        try { if (this._sentinel) { var s = this._sentinel; this._sentinel = null; s.release(); } } catch (e) {}
    }
};

// Wake Lock auto-releases when the page hides — re-acquire on return while a
// grade is still in flight.
document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    if (window.gradingInProgress) pgcWakeLock.acquire();
    // A suspended tab froze mid-submit with the modal open — kick the recovery
    // poll and the demo progress poll so a restored tab finishes the grade
    // instead of spinning on a dead XHR forever.
    var glm = document.getElementById('grading-loading-modal');
    if (!glm) return;
    if (typeof glm._pgcWakePoll === 'function') glm._pgcWakePoll();
    if (typeof glm._restartGradeProgressPoll === 'function') glm._restartGradeProgressPoll();
});
// bfcache restore fires pageshow (a discard→reload re-runs DOMContentLoaded
// fresh instead, which the recheck below already covers).
window.addEventListener('pageshow', function () {
    var glm = document.getElementById('grading-loading-modal');
    if (!glm) return;
    if (typeof glm._pgcWakePoll === 'function') glm._pgcWakePoll();
    if (typeof glm._restartGradeProgressPoll === 'function') glm._restartGradeProgressPoll();
});


// ── Return-visit pending-grade recovery ──
// A user who navigates away mid-grade kills the in-page poller, but the grade
// still completes server-side and lands in action_history. On the next grade-
// page load, ask grade-status whether a newer completed record exists than
// the last one this browser rendered, and banner it so the paid result gets
// found instead of sitting unseen in history.
document.addEventListener('DOMContentLoaded', function () {
    var form = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
    if (!form) return;
    var since = Math.floor(Date.now() / 1000) - 7200; // last 2h
    fetch('/api/grade-status.php?since=' + since + '&via=recheck', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (st) {
            if (!st || !st.success || st.status !== 'done' || !st.grade) return;
            var aid = parseInt(st.grade.action_id, 10) || 0;
            if (!aid) return;
            var isSeen = function () {
                var seen = 0, dismissed = 0;
                try {
                    seen = parseInt(localStorage.getItem('pgcSeenGradeId') || '0', 10);
                    dismissed = parseInt(localStorage.getItem('pgcPendingDismissedId') || '0', 10);
                } catch (e) {}
                return aid <= Math.max(seen, dismissed);
            };
            if (isSeen()) return;
            var markSeen = function () {
                try {
                    localStorage.setItem('pgcSeenGradeId', String(aid));
                    localStorage.setItem('pgcPendingDismissedId', String(aid));
                } catch (e) {}
            };
            if (typeof logFunnelEvent === 'function') logFunnelEvent('grade_pending_found', { action_id: aid });
            var cardName = '';
            try {
                var rec = st.grade.data && st.grade.data.records && st.grade.data.records[0];
                var idn = rec && (rec.identification || (rec.card && rec.card.identification));
                cardName = (idn && (idn.full_name || idn.name)) || (rec && rec.name) || '';
            } catch (e) {}
            var banner = document.createElement('div');
            // grid-column:1/-1 — the banner is inserted into the .grid-2
            // container, where a default-width item would steal the left
            // column and displace the form into the right.
            banner.style.cssText = 'grid-column:1/-1;background:#ecfdf5;border:2px solid #10b981;color:#065f46;padding:14px 44px 14px 18px;border-radius:12px;margin:0 auto 16px;max-width:640px;font-weight:600;font-size:14px;line-height:1.4;position:relative;text-align:left;';
            banner.appendChild(document.createTextNode(
                cardName ? 'Your ' + cardName + ' finished grading while you were away — '
                         : 'A card you submitted finished grading while you were away — '));
            var link = document.createElement('a');
            link.href = '/dashboard/history/' + aid;
            link.style.cssText = 'color:#047857;font-weight:800;text-decoration:underline;';
            link.textContent = 'view your result';
            // Viewing the result IS acknowledging it — record the aid so the
            // banner can't resurface (the API serves the grade for ~15min).
            link.addEventListener('click', markSeen);
            banner.appendChild(link);
            var x = document.createElement('span');
            x.innerHTML = '&times;';
            x.style.cssText = 'position:absolute;top:8px;right:12px;font-size:20px;cursor:pointer;opacity:0.6;line-height:1;';
            x.onclick = function () {
                markSeen();
                banner.remove();
            };
            banner.appendChild(x);
            var anchor = document.getElementById('grading-form') || form.parentElement;
            anchor.parentElement.insertBefore(banner, anchor);
            // bfcache restores the DOM (banner included) without re-running
            // DOMContentLoaded — re-check on pageshow so Back after viewing
            // doesn't resurrect it.
            window.addEventListener('pageshow', function () {
                if (isSeen()) banner.remove();
            });
        })
        .catch(function () {});
});

// ==================== Pending-scan stash (paid-return resume) ====================
// A File survives navigation via IndexedDB — a user who hits the credit wall
// mid-scan pays, returns, and shouldn't have to re-photograph their card.
const PGC_PENDING_SCAN_DB = 'pgc-pending-scan';
// Captured at deferred-eval time — the footer's paid-confirmation handler
// strips ?paid=1 before DOMContentLoaded listeners run.
const PGC_PAID_RETURN = (function () {
    try { return new URLSearchParams(location.search).get('paid') === '1'; } catch (_) { return false; }
})();
// Photo Check → "Grade This Card" hands its checked photo over through the
// same stash (handoff:1) so the user never re-uploads what they just checked.
const PGC_PHOTO_HANDOFF = (function () {
    try { return new URLSearchParams(location.search).get('from') === 'photo-check'; } catch (_) { return false; }
})();

// Test-only handle on the modal builders (top-level functions in this file)
// so the Playwright viewport audit can open them without driving the full
// grade submission.
window.PGC_TEST_HOOKS = {
    showGradingModal: showGradingModal,
    hideGradingModal: hideGradingModal,
    showUploadRejectedModal: showUploadRejectedModal,
    startGradeProgressPoll: startGradeProgressPoll,
    showGradeReveal: showGradeReveal
};

function pgcPendingScanStore(mode) {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(PGC_PENDING_SCAN_DB, 1);
        req.onupgradeneeded = () => req.result.createObjectStore('scans');
        req.onsuccess = () => resolve(req.result.transaction('scans', mode).objectStore('scans'));
        req.onerror = () => reject(req.error);
    });
}

function pgcStashPendingScan(frontInput, backInput, fromUpsell) {
    try {
        if (!window.indexedDB) return;
        const front = frontInput && (frontInput._heicFile || (frontInput.files && frontInput.files[0]));
        if (!front) return;
        const back = backInput ? (backInput._heicFile || (backInput.files && backInput.files[0]) || null) : null;
        pgcPendingScanStore('readwrite').then(store => {
            store.put({ front: front, back: back, frontSize: front.size, backSize: back ? back.size : 0, ts: Date.now(), path: location.pathname, upsell: fromUpsell ? 1 : 0 }, 'latest');
        }).catch(() => {});
    } catch (_) {}
}

// A buyer who paid from the credits page (not the inline 402 modal) has
// nothing stashed, so the paid return used to land them on a bare empty form
// with no next step - 12% of buyers never spent a credit. Give them an
// explicit starting point that names the balance they just bought.
function pgcShowPaidReadyState() {
    if (!PGC_PAID_RETURN) return;
    try {
        if (typeof logFunnelEvent === 'function') logFunnelEvent('paid_return_no_stash', { page: location.pathname });
        const form = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
        if (!form || document.getElementById('pgc-paid-ready')) return;
        const params = new URLSearchParams(location.search);
        let bal = window.CREDIT_BALANCE != null ? Number(window.CREDIT_BALANCE) : null;
        const qCredits = parseFloat(params.get('credits'));
        if ((bal == null || isNaN(bal) || bal <= 0) && !isNaN(qCredits) && qCredits > 0) {
            bal = qCredits;
        }
        const per = parseFloat(window.GRADE_CREDITS) || 1;
        const grades = bal != null && !isNaN(bal) ? Math.floor(bal / per) : null;

        const d = document.createElement('div');
        d.id = 'pgc-paid-ready';
        d.setAttribute('role', 'status');
        d.style.cssText = 'display:flex;align-items:center;gap:14px;background:#ecfdf5;border:2px solid #10b981;'
            + 'border-radius:12px;padding:14px 16px;margin:0 0 16px;color:#065f46;font-size:14px;line-height:1.45;'
            + 'box-shadow:0 1px 2px rgba(6,95,70,.08);';

        const icon = document.createElement('div');
        icon.setAttribute('aria-hidden', 'true');
        icon.style.cssText = 'flex:0 0 auto;width:34px;height:34px;border-radius:50%;background:#10b981;color:#fff;'
            + 'display:flex;align-items:center;justify-content:center;font-size:19px;font-weight:800;';
        icon.textContent = '\u2713';

        const body = document.createElement('div');
        body.style.cssText = 'flex:1 1 auto;min-width:0;';
        const title = document.createElement('div');
        title.style.cssText = 'font-weight:800;font-size:15px;';
        const shownBal = bal != null && !isNaN(bal) ? Math.round(bal * 100) / 100 : null;
        const T = (window.PGC_LANG === 'es' && window.PGC_T) || {};
        const t = function (k, en) { const s = T[k]; if (!s) return en; const extra = Array.prototype.slice.call(arguments, 2); let i = 0; return s.replace(/%s/g, function () { return String(extra[i++]); }); };
        title.textContent = shownBal == null
            ? t('paid.ready.title.any', 'Your credits are ready')
            : (shownBal === 1 ? t('paid.ready.title1', 'Your 1 credit is ready') : t('paid.ready.title', 'Your ' + shownBal + ' credits are ready', shownBal));
        const sub = document.createElement('div');
        sub.style.cssText = 'margin-top:2px;font-weight:600;color:#047857;';
        sub.textContent = grades != null
            ? (grades === 1
                ? t('paid.ready.sub1', 'That\u2019s 1 complete grade \u2014 add a card photo below to start.')
                : t('paid.ready.sub', 'That\u2019s ' + grades + ' complete grades \u2014 add a card photo below to start.', grades))
            : t('paid.ready.sub.any', 'Add a card photo below to start.');
        body.appendChild(title);
        body.appendChild(sub);

        const lastCard = params.get('last_card');
        if (lastCard && /^\d+$/.test(lastCard)) {
            const histLink = document.createElement('div');
            histLink.style.cssText = 'margin-top:6px;font-size:12px;font-weight:600;';
            histLink.innerHTML = 'Looking for your last card? <a href="/dashboard/history/' + lastCard + '" style="color:#047857;text-decoration:underline;font-weight:700;">View #' + lastCard + ' in History \u2192</a>';
            body.appendChild(histLink);
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = t('paid.ready.btn', 'Add photo');
        btn.style.cssText = 'flex:0 0 auto;background:#059669;color:#fff;border:0;border-radius:9px;padding:10px 16px;'
            + 'font-weight:800;font-size:14px;cursor:pointer;';
        btn.addEventListener('click', () => {
            const zone = document.getElementById('upload-zone-front');
            if (!zone) return;
            zone.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const camBtn = zone.querySelector('.use-camera-btn');
            const inp = form.querySelector('input[name="image_front"]');
            if (camBtn && window.innerWidth < 768) {
                camBtn.click();
            } else if (inp) {
                inp.click();
            }
        });

        d.appendChild(icon);
        d.appendChild(body);
        d.appendChild(btn);
        form.parentNode.insertBefore(d, form);

        const zone = document.getElementById('upload-zone-front');
        if (zone) {
            const prev = zone.style.boxShadow;
            zone.style.boxShadow = '0 0 0 3px #10b981';
            setTimeout(() => { zone.style.boxShadow = prev; }, 2400);
        }
        // On phones the ready card and upload zone land just under the fold behind the hero —
        // scroll the upload zone directly into view so the camera and upload buttons are immediately actionable.
        setTimeout(function () {
            try {
                const z = document.getElementById('upload-zone-front') || d;
                z.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (_) {}
        }, 300);
    } catch (_) {}
}

function pgcRestorePendingScan() {
    try {
        if (!window.indexedDB || typeof DataTransfer === 'undefined') { pgcShowPaidReadyState(); return; }
        // No early return for ordinary loads: a soft stash (staged photos,
        // no handoff/upsell flag) restores on ANY same-path reload — Android
        // kills the tab mid-picker and reloads on return, silently wiping
        // staged files. Paid/handoff stashes keep their original gating.
        pgcPendingScanStore('readonly').then(store => {
            const get = store.get('latest');
            get.onerror = () => pgcShowPaidReadyState();
            get.onsuccess = () => {
                const s = get.result;
                // Fresh (<30min) and same tool page only.
                if (!s || s.path !== location.pathname || (Date.now() - s.ts) > 30 * 60 * 1000) {
                    pgcShowPaidReadyState();
                    return;
                }
                // Soft stashes (staged photos lost to a reload — no handoff,
                // no upsell) restore on any load. Everything else keeps the
                // paid-return/handoff gate.
                const softStash = !s.handoff && !s.upsell;
                // A finished result is on screen (?result=N): the staged photo
                // was already graded. Re-attaching it showed "Your card photo is
                // still attached — tap Grade" over the report and invited a
                // duplicate paid grade (owner test purchase 2026-11-01, #12594).
                if (softStash && /[?&]result=\d+/.test(location.search)) {
                    pgcPendingScanStore('readwrite').then(st => st.delete('latest')).catch(() => {});
                    return;
                }
                if (!softStash && !PGC_PAID_RETURN && !s.handoff) return;
                // WebKit IDB blob persistence loses a File's backing bytes:
                // the restored object keeps its name but .size reads ~0 while
                // multipart sends a ~35B stub → server "could not read image
                // dimensions" and the auto-resubmit re-feeds the corpse. No real
                // card photo is under 1KB; a stored-size mismatch = partial loss.
                const fileOk = (file, expectedSize) => {
                    if (!file) return null;
                    // Blob.prototype.bytes() (Baseline 2025) makes file.bytes a
                    // truthy FUNCTION on real Files — the old `if (file.bytes)`
                    // then rebuilt the File from its source text (~35B corpse,
                    // "function bytes() { [native code] }") → server rejected
                    // "could not read image dimensions". Photo Check's stash is
                    // a PLAIN object {bytes,name,type}; only rebuild those.
                    if (!(file instanceof Blob) && file.bytes) {
                        file = new File([file.bytes], file.name || 'photo.jpg', { type: file.type || 'image/jpeg' });
                    }
                    if (file.size >= 1024 && (!expectedSize || file.size === expectedSize)) return file;
                    if (typeof logFunnelEvent === 'function') logFunnelEvent('paid_return_stash_corrupt', {
                        page: location.pathname, restored_size: file.size || 0, expected_size: expectedSize || 0,
                    });
                    return null;
                };
                // Corrupt stash → drop it and show the normal paid-ready state
                // instead of attaching a shell or auto-submitting a corpse.
                if (s.front && !fileOk(s.front, s.frontSize)) {
                    pgcPendingScanStore('readwrite').then(st => st.delete('latest')).catch(() => {});
                    if (window.Toast) Toast.info('Your photo didn\'t survive the checkout round-trip — please re-attach it.', 9000);
                    pgcShowPaidReadyState();
                    return;
                }
                const setFile = (name, file, verdict) => {
                    if (!file) return false;
                    // Photo Check stores raw bytes (plain object) — WebKit can
                    // refuse Blobs in IndexedDB. Never rebuild a real File:
                    // Blob.prototype.bytes() is a truthy method, not data.
                    if (!(file instanceof Blob) && file.bytes) {
                        file = new File([file.bytes], file.name || 'photo.jpg', { type: file.type || 'image/jpeg' });
                    }
                    const inp = document.querySelector('input[name="' + name + '"]');
                    if (!inp) return false;
                    try {
                        const dt = new DataTransfer();
                        dt.items.add(file);
                        // upload-handler.js uses Photo Check's verdict instead of
                        // its local blur heuristic, so the two can't disagree.
                        inp._pcVerdict = verdict ? { sig: file.name + ':' + file.size, v: verdict } : null;
                        inp.files = dt.files;
                        inp.dispatchEvent(new Event('change', { bubbles: true }));
                        return true;
                    } catch (_) { return false; }
                };
                // Attach each slot independently — a soft stash may hold only
                // the back (u5074) and the early return below would orphan it.
                // No upload form at all (guest gate) → keep the stash for
                // after sign-in.
                const attachedF = s.front ? setFile('image_front', fileOk(s.front, s.frontSize), s.handoff ? s.verdict : null) : false;
                const attachedB = setFile('image_back', fileOk(s.back, s.backSize));
                if (!attachedF && !attachedB) return;
                const pcHint = s.handoff && attachedF && document.getElementById('pgc-photo-check-hint');
                if (pcHint) {
                    const sc = s.verdict && s.verdict.score != null ? ' (photo score ' + parseInt(s.verdict.score, 10) + ')' : '';
                    pcHint.textContent = '✓ This photo already went through Photo Check' + sc + '. To use a different photo, tap ✕ on it.';
                }
                pgcPendingScanStore('readwrite').then(st => st.delete('latest')).catch(() => {});
                if (typeof logFunnelEvent === 'function') logFunnelEvent(
                    s.handoff ? 'photo_check_handoff_restored' : (s.upsell ? 'paid_return_scan_restored' : 'staged_photos_restored'),
                    { page: location.pathname, front: attachedF ? 1 : 0, back: attachedB ? 1 : 0 });
                if (window.Toast) {
                    Toast.info(s.handoff
                        ? 'Your checked photo is attached as the front — add a back photo if you like, then tap Grade.'
                        : (attachedF && attachedB
                            ? 'Your card photos are still attached — tap Grade to finish your scan.'
                            : 'Your card photo is still attached — tap Grade to finish your scan.'), 9000);
                }
                const zone = document.getElementById('upload-zone-front');
                if (zone) zone.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Auto-resubmit: this scan was stashed by the 402 upsell flow
                // (upsell:1) and the user just paid — fire the grade again
                // instead of leaving them to re-click. Guarded so it never
                // fires for a general ?paid=1 return (upsell flag absent), never
                // while a grade is already running, and never while the upsell
                // modal is open. The stash was deleted above — one shot.
                if (s.upsell) {
                    const form = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
                    const modalOpen = !!(window.CreditUpsell && typeof window.CreditUpsell.isOpen === 'function' && window.CreditUpsell.isOpen());
                    if (form && !window.gradingInProgress && !modalOpen) {
                        setTimeout(() => {
                            if (window.gradingInProgress || (window.CreditUpsell && typeof window.CreditUpsell.isOpen === 'function' && window.CreditUpsell.isOpen())) return;
                            try {
                                form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                if (typeof logFunnelEvent === 'function') logFunnelEvent('paid_return_auto_submit', { page: location.pathname });
                            } catch (e) {
                                if (typeof logFunnelEvent === 'function') logFunnelEvent('paid_return_auto_submit_failed', { error: String(e && e.message || e).slice(0, 120) });
                            }
                        }, 600);
                    }
                }
            };
        }).catch(() => {});
    } catch (_) {}
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pgcRestorePendingScan);
} else {
    pgcRestorePendingScan();
}

// ── Live total-cost chip ────────────────────────────────────────────────────
// The form badges show the front (1) and back (+0.5) costs separately; this
// keeps the bottom "credits per complete grade" chip in sync with the actual
// staged inputs so a user who adds a back photo sees the real total (1.5)
// before submitting — the 402 upsell then never surprises them.
function pgcMountTotalChip() {
    const form = document.querySelector('#grading-form form.upload-form') || document.querySelector('.upload-form');
    if (!form) return;
    const frontCost = parseFloat(form.dataset.serviceCost);
    const backCost = parseFloat(form.dataset.backCost);
    const chipText = document.getElementById('pgc-grade-total-text');
    if (!chipText || isNaN(frontCost)) return;
    const backInput = form.querySelector('input[name="image_back"]');
    const render = () => {
        const hasBack = !!(backCost > 0 && backInput && backInput.files && backInput.files.length > 0);
        if (hasBack) {
            const total = frontCost + backCost;
            chipText.textContent = total + (total === 1 ? ' credit' : ' credits') + ' per complete grade (front ' + frontCost + ' + back ' + backCost + ')';
        } else {
            chipText.textContent = frontCost + (frontCost === 1 ? ' credit' : ' credits') + ' per complete grade';
        }
    };
    if (backInput) backInput.addEventListener('change', render);
    render();
}
document.addEventListener('DOMContentLoaded', pgcMountTotalChip);

window.PgcJS=window.PgcJS||{};PgcJS["grade-v3"]=1;
